// COMPLETE FILE: src/components/EventDashboard.js
// Updated to log WebSocket event detail updates for debugging
// App Version: 1.0.38

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Sun, Moon, Calendar, Volume2, VolumeX, RotateCcw, List, Bell, BellOff, MoreVertical, RefreshCw, Archive, ArrowUpDown } from 'lucide-react';
import EventCard from './EventCard';
import SystemHealth from './SystemHealth';
import NotificationHistory from './NotificationHistory';
import OrderEventsModal from './OrderEventsModal';
import UpdateAvailable from './UpdateAvailable';
import OccupancyCard from './OccupancyCard';
import { fetchUpcomingEvents, fetchEventDetail, fetchEventDetailsBatch, fetchMetaCampaigns, fetchPastEvents, fetchPastEventDetail, fetchOccupancy } from '../utils/api';
import { fetchLiveCounts, fetchWidgetEventIds } from '../utils/widgetStats';
import { useWebSocket } from '../hooks/useWebSocket';
import { parsePrice, matchEventWithCampaigns, detectTicketStructure, normalizeTierName } from '../utils/helpers';
import { mergeTixrDetail } from '../utils/tixrMerge';
import { UPCOMING_EVENTS_QUERY } from '../utils/queries';
import { HOST_ID } from '../utils/constants';
import { classifyRate, isComp, matchOverrideKey } from '../utils/ticketModel';
import pushNotificationManager from '../utils/pushNotifications';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';

// Log app version to verify correct code is loaded
const APP_VERSION = '1.1.27';
console.log(`📱 EventDashboard loaded - App Version: ${APP_VERSION}`);

const EventDashboard = () => {
  // Parse URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const hideHeader = urlParams.get('hideHeader') === 'true';
  const limitParam = urlParams.get('limit');
  const eventLimit = limitParam ? parseInt(limitParam, 10) : null;
  const venueFilter = urlParams.get('venue')?.toLowerCase();
  
  // Debug: Log URL parameters (remove this after testing)
  console.log('Event Dashboard URL Params:', {
    hideHeader,
    limit: eventLimit,
    venue: venueFilter,
    search: window.location.search
  });
  
  // ALL your existing state - preserved exactly
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null); // { message, type }, in-UI replacement for window.alert (prohibited)
  const toastTimerRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingEvents, setLoadingEvents] = useState(new Set());
  const [expandedEvents, setExpandedEvents] = useState(new Set());
  const [eventLastRefreshed, setEventLastRefreshed] = useState({});
  const [eventRevenueDisplay, setEventRevenueDisplay] = useState({});
  const [cacheStatus, setCacheStatus] = useState(null);
  const [eventPriceDisplay, setEventPriceDisplay] = useState({});
  const [expandedSections, setExpandedSections] = useState({});
  const [metaCampaigns, setMetaCampaigns] = useState([]);
  const [liveCounts, setLiveCounts] = useState({});         // eventId -> liveNow (frequent)
  const [widgetEventIds, setWidgetEventIds] = useState(() => new Set()); // events with widget data (gate, slow)
  useEffect(() => {
    let on = true;
    const loadLive = () => fetchLiveCounts().then((c) => { if (on) setLiveCounts(c); }).catch(() => {});
    const loadGate = () => fetchWidgetEventIds().then((s) => { if (on) setWidgetEventIds(s); }).catch(() => {});
    loadLive(); loadGate();
    const tLive = setInterval(loadLive, 20000);   // live-now: every 20s (interim; ws-push later)
    const tGate = setInterval(loadGate, 120000);  // which events have widget data: every 2 min
    return () => { on = false; clearInterval(tLive); clearInterval(tGate); };
  }, []);
  const [loadingMetaCampaigns, setLoadingMetaCampaigns] = useState(false);
  const [metaCampaignsLastUpdated, setMetaCampaignsLastUpdated] = useState(null); // eslint-disable-line no-unused-vars
  const [metaApiError, setMetaApiError] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  // Pull-to-refresh (mobile): distance the user has pulled past the top.
  const [pullDistance, setPullDistance] = useState(0);
  // Live door occupancy (security check-in): payload for tonight's event, and
  // which card is currently flipped to its occupancy face.
  const [occupancy, setOccupancy] = useState(null);
  const [flippedEventId, setFlippedEventId] = useState(null);
  // Past-event historical occupancy_data, lazy-fetched on flip (eventId → data).
  const [historicalOcc, setHistoricalOcc] = useState({});
  // Venue has door data at all → every past card gets the flip affordance;
  // snapshot-less events (e.g. ended earlier today) compose lazily on flip.
  const [occupancySupported, setOccupancySupported] = useState(false);

  // Flip a past card: fetch its stored occupancy snapshot once, then flip.
  const flipPastCard = useCallback(async (eventId) => {
    if (flippedEventId === eventId) { setFlippedEventId(null); return; }
    if (!historicalOcc[eventId]) {
      try {
        const raw = await fetchPastEventDetail(eventId);
        if (raw?.occupancy_data) setHistoricalOcc(prev => ({ ...prev, [eventId]: raw.occupancy_data }));
      } catch { /* leave unflipped on failure */ }
    }
    setFlippedEventId(eventId);
  }, [flippedEventId, historicalOcc]);
  const pullStartYRef = useRef(null);
  const PULL_THRESHOLD = 60;
  const [expandedAdSets, setExpandedAdSets] = useState({});
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    if (saved !== null) {
      return saved === 'true';
    }
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // WebSocket state and preferences
  const [useWebSocketEnabled, setUseWebSocketEnabled] = useState(() => {
    const saved = localStorage.getItem('useWebSocket');
    return saved !== null ? saved === 'true' : true;
  });
  const [connectionMethod, setConnectionMethod] = useState('http');
  const [recentlyUpdatedEvents, setRecentlyUpdatedEvents] = useState(new Set());
  const [ticketChanges, setTicketChanges] = useState({});
  const [categoryChanges, setCategoryChanges] = useState({});
  const [audioEnabled, setAudioEnabled] = useState(() => {
    const saved = localStorage.getItem('audioNotifications');
    return saved !== null ? saved === 'true' : false;
  });
  
  // Notification history state - persisted to localStorage
  const [notificationHistory, setNotificationHistory] = useState(() => {
    try {
      const saved = localStorage.getItem('notificationHistory');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Filter out notifications older than 7 days
        const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
        return parsed.filter(n => new Date(n.timestamp).getTime() > sevenDaysAgo);
      }
    } catch (err) {
      console.error('Failed to load notification history:', err);
    }
    return [];
  });

  // Push notification state
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSubscribing, setPushSubscribing] = useState(false);

  // Update available state
  const [updateAvailable, setUpdateAvailable] = useState(false);

  // Drag and drop state - persisted to localStorage
  const [customEventOrder, setCustomEventOrder] = useState(() => {
    try {
      const saved = localStorage.getItem('customEventOrder');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (err) {
      console.error('Failed to load custom event order:', err);
    }
    return null;
  });
  const [activeId, setActiveId] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const [showDebugPanel, setShowDebugPanel] = useState(false);

  // Past events archive state
  const [activeTab, setActiveTab] = useState('upcoming');
  const [pastEvents, setPastEvents] = useState([]);
  const [pastEventsLoaded, setPastEventsLoaded] = useState(false);
  const [pastEventsLoading, setPastEventsLoading] = useState(false);
  // Server-side pagination for the past archive (limit/offset + total). pastTotal
  // drives the "Past (N)" label and is fetched count-only on mount.
  const [pastTotal, setPastTotal] = useState(0);
  const [pastHasMore, setPastHasMore] = useState(false);
  const [pastLoadingMore, setPastLoadingMore] = useState(false);
  const PAST_PAGE_SIZE = 50;
  // Count of past rows currently loaded == the next page's offset (server pages
  // are contiguous). A ref so infinite-scroll appends read the latest value
  // synchronously without a stale closure.
  const pastLoadedCountRef = useRef(0);
  // Mirror of pastEvents so the expand handler can look up a card's current
  // header-only status without re-creating the handler on every page append.
  const pastEventsRef = useRef([]);
  // Past-detail fetches in flight (per eventId) — dedupes rapid expand toggles.
  const loadingPastDetailRef = useRef(new Set());
  // Bottom-of-list sentinel for infinite scroll on the past archive.
  const pastSentinelRef = useRef(null);
  // Signature of the in-flight/last past fetch ({sort|dir|q}); a slow response
  // whose signature ≠ the current one is ignored (stale-response guard).
  const pastFetchSigRef = useRef('');
  const [sortBy, setSortBy] = useState('date'); // 'date', 'revenue', 'sold'
  const [sortDir, setSortDir] = useState('desc'); // 'asc', 'desc'

  // Per-event load errors (eventId -> message). Cleared when a fetch succeeds.
  const [eventLoadErrors, setEventLoadErrors] = useState({});
  // Mirror of eventSubscriptionsRef.current keys in state so the UI re-renders
  // when subscriptions are added/removed (refs alone don't trigger a re-render).
  const [subscribedEventIds, setSubscribedEventIds] = useState(() => new Set());

  // Enhanced WebSocket hook
  const {
    isConnected: wsConnected,
    connectionStatus: wsStatus,
    connectionQuality,
    error: wsError,
    connectedClients,
    wsUrl,
    subscribeToEvents,
    subscribeToEventDetail,
    subscribeToMetaCampaigns,
    reconnect: wsReconnect,
    subscriptionCount
  } = useWebSocket(useWebSocketEnabled);

  // ALL your existing refs - preserved exactly
  const loadingQueueRef = useRef(new Set());
  const observerRef = useRef(null);
  const eventsRef = useRef([]);
  const eventsLoadedViaHTTP = useRef(new Set());
  const wsConnectedRef = useRef(false);
  const eventSubscriptionsRef = useRef(new Map());
  const metaCampaignSubscriptionRef = useRef(null);
  const audioRef = useRef(null);
  const eventPollingIntervalRef = useRef(null);
  const headerMenuRef = useRef(null);
  // Last seen updatedAt per event (epoch ms). Used to drop stale WS pushes that
  // arrive after a fresher REST payload has already been applied.
  const lastEventUpdateRef = useRef(new Map());
  // Ring buffer of recently-applied content hashes per event. If incoming data
  // matches a hash we already moved past, it's stale - skip it. Defense against
  // any path that delivers older content with a fresh updatedAt.
  const lastEventHashRingRef = useRef(new Map());

  // Drag and drop sensors with iOS optimization
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px of movement required before drag starts
      },
    }),
    useSensor(TouchSensor, {
      // iOS-optimized settings - balanced for responsiveness and stability
      activationConstraint: {
        delay: 50, // 50ms delay before drag starts (reduced for faster response)
        tolerance: 5, // 5px tolerance for touch movement (reduced for easier dragging)
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Keep refs in sync - exactly as before
  useEffect(() => {
    eventsRef.current = events;
  }, [events]);

  useEffect(() => {
    wsConnectedRef.current = wsConnected;
  }, [wsConnected]);
  

  // Save WebSocket preference
  useEffect(() => {
    localStorage.setItem('useWebSocket', useWebSocketEnabled);
  }, [useWebSocketEnabled]);

  // Save audio preference
  useEffect(() => {
    localStorage.setItem('audioNotifications', audioEnabled);
  }, [audioEnabled]);

  // Save dark mode preference
  useEffect(() => {
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  // Initialize push notifications and check subscription status
  useEffect(() => {
    const checkPushSubscription = async () => {
      try {
        if (!pushNotificationManager.isSupported()) {
          console.log('[Push] Push notifications not supported in this browser');
          return;
        }

        const isSubscribed = await pushNotificationManager.isSubscribed();
        setPushEnabled(isSubscribed);
        console.log('[Push] Initial subscription status:', isSubscribed);
      } catch (error) {
        console.error('[Push] Error checking subscription status:', error);
      }
    };

    checkPushSubscription();
  }, []);

  // Handle push notification clicks - scroll to and expand the relevant event
  useEffect(() => {
    pushNotificationManager.setupMessageListener((data) => {
      console.log('[Push] Notification clicked:', data);
      if (data.eventId) {
        // Switch to upcoming tab
        setActiveTab('upcoming');
        // Expand the event
        setExpandedEvents(prev => new Set(prev).add(data.eventId));
        // Scroll to the event card
        setTimeout(() => {
          const el = document.querySelector(`[data-event-id="${data.eventId}"]`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 300);
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Setup update detection
  useEffect(() => {
    // Register callback for when update is available
    pushNotificationManager.onUpdateAvailable(() => {
      console.log('[App] 🎉 Update available - showing update banner now!');
      setUpdateAvailable(true);
    });

    // Register service worker if supported
    if (pushNotificationManager.isSupported()) {
      console.log('[App] Registering service worker for update detection');
      pushNotificationManager.registerServiceWorker().catch(error => {
        console.error('[App] Failed to register service worker:', error);
      });
    }
  }, []);

  // In-UI toast (window.alert is prohibited: it blocks the thread and is
  // broken on wall displays / standalone PWA).
  const showToast = useCallback((message, type = 'info', ms = 8000) => {
    setToast({ message, type });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    if (ms) toastTimerRef.current = setTimeout(() => setToast(null), ms);
  }, []);

  // Handle push notification toggle
  const handleTogglePushNotifications = async () => {
    if (pushSubscribing) return;

    try {
      setPushSubscribing(true);

      if (pushEnabled) {
        // Unsubscribe
        console.log('[Push] Unsubscribing from push notifications...');
        await pushNotificationManager.unsubscribe();
        setPushEnabled(false);
        console.log('[Push] Successfully unsubscribed');
      } else {
        // Subscribe
        console.log('[Push] Subscribing to push notifications...');
        await pushNotificationManager.subscribe();
        setPushEnabled(true);
        console.log('[Push] Successfully subscribed');
      }
    } catch (error) {
      console.error('[Push] Error toggling push notifications:', error);

      // Show user-friendly error message
      let message = 'Failed to enable push notifications';
      if (error.message.includes('denied')) {
        message = 'Push notification permission was denied. Please enable it in your browser settings.';
      } else if (error.message.includes('unsupported')) {
        message = 'Push notifications are not supported in this browser.';
      }

      showToast(message, 'error');
    } finally {
      setPushSubscribing(false);
    }
  };

  // Handle update activation
  const handleUpdate = () => {
    console.log('[App] User clicked update - activating waiting service worker');
    pushNotificationManager.activateUpdate();
  };

  // Handle update banner dismissal
  const handleDismissUpdate = () => {
    console.log('[App] User dismissed update banner');
    setUpdateAvailable(false);
  };

  // Enable audio on first user interaction
  useEffect(() => {
    const enableAudio = () => {
      if (audioRef.current && audioEnabled) {
        // Try to play a silent sound to enable audio
        audioRef.current.volume = 0;
        audioRef.current.play()
          .then(() => {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            audioRef.current.volume = 0.3;
            console.log('🔊 Audio enabled after user interaction');
            // Remove the listener after first interaction
            document.removeEventListener('click', enableAudio);
          })
          .catch(() => {
            // Ignore errors, will try again on next interaction
          });
      }
    };

    // Add listener for first user interaction
    if (audioEnabled) {
      document.addEventListener('click', enableAudio);
    }

    return () => {
      document.removeEventListener('click', enableAudio);
    };
  }, [audioEnabled]);

  // Function to play notification sound and vibrate
  const playNotificationSound = useCallback(() => {
    if (!audioEnabled) return;
    
    // Vibrate on supported devices (iOS and Android)
    if ('vibrate' in navigator) {
      try {
        // Vibrate for 200ms
        navigator.vibrate(200);
        console.log('📳 Device vibrated');
      } catch (err) {
        console.log('📳 Vibration not supported or failed:', err);
      }
    }
    
    // Play sound if audio element is available
    if (audioRef.current) {
      console.log('🔊 Playing notification sound');
      try {
        audioRef.current.volume = 0.3; // 30% volume
        audioRef.current.currentTime = 0;
        audioRef.current.play()
          .then(() => {
            console.log('🔊 Notification sound played successfully');
          })
          .catch(err => {
            if (err.name === 'NotAllowedError') {
              console.log('🔊 Audio autoplay blocked - user interaction required');
              // Don't log as error, this is expected browser behavior
            } else {
              console.error('🔊 Failed to play notification sound:', err);
            }
          });
      } catch (err) {
        console.error('🔊 Error playing notification sound:', err);
      }
    }
  }, [audioEnabled]);

  // Update connection method based on WebSocket status
  useEffect(() => {
    console.log(`🔌 WebSocket status - enabled: ${useWebSocketEnabled}, connected: ${wsConnected}, status: ${wsStatus}`);
    if (useWebSocketEnabled) {
      if (wsConnected) {
        setConnectionMethod('hybrid');
      } else if (wsStatus === 'connecting') {
        setConnectionMethod('connecting');
      } else if (wsStatus === 'disabled') {
        setConnectionMethod('disabled');
      } else {
        setConnectionMethod('http');
      }
    } else {
      setConnectionMethod('http');
    }
  }, [useWebSocketEnabled, wsConnected, wsStatus]);

  // Load Meta campaigns - exactly as before
  const loadMetaCampaigns = useCallback(async () => {
    if (loadingMetaCampaigns) return;
    
    try {
      setLoadingMetaCampaigns(true);
      setMetaApiError(null);
      
      const data = await fetchMetaCampaigns();
      
      if (data.success && data.data) {
        console.log(`Loaded ${data.data.length} Meta campaigns via HTTP`);
        setMetaCampaigns(data.data);
        setMetaCampaignsLastUpdated(new Date());
      } else if (data.error) {
        setMetaApiError(data.error);
      }
    } catch (err) {
      console.error('Error loading Meta campaigns:', err);
      setMetaApiError(err.message);
    } finally {
      setLoadingMetaCampaigns(false);
    }
  }, [loadingMetaCampaigns]);

  // WebSocket Meta campaigns subscription - exactly as before
  useEffect(() => {
    if (wsConnected && useWebSocketEnabled) {
      console.log('📡 Setting up WebSocket Meta campaigns subscription');
      
      if (metaCampaignSubscriptionRef.current) {
        metaCampaignSubscriptionRef.current();
        metaCampaignSubscriptionRef.current = null;
      }
      
      const unsubscribe = subscribeToMetaCampaigns((data) => {
        console.log('📡 Received Meta campaigns update via WebSocket');
        
        if (data.success && data.data) {
          setMetaCampaigns(data.data);
          setMetaCampaignsLastUpdated(new Date());
          setMetaApiError(null);
        } else if (data.error) {
          setMetaApiError(data.error);
        }
      });
      
      metaCampaignSubscriptionRef.current = unsubscribe;
      
      return () => {
        if (metaCampaignSubscriptionRef.current) {
          metaCampaignSubscriptionRef.current();
          metaCampaignSubscriptionRef.current = null;
        }
      };
    }
  }, [wsConnected, useWebSocketEnabled, subscribeToMetaCampaigns]);

  // Helper function to process events data - exactly as before
  const processEventsData = useCallback((upcomingEvents) => {
    const processedEvents = upcomingEvents
      .filter(event => event.timeSlots?.nodes?.[0]?.id)
      .map(event => ({
        id: event.id,
        title: event.title,
        date: event.timeSlots.nodes[0].startAt?.split('T')[0] || 'TBD',
        startAt: event.timeSlots.nodes[0].startAt || null, // full instant, occupancy 2h-before-doors gate
        timeslotId: event.timeSlots.nodes[0].id,
        isHeaderOnly: true,
        capacity: event.maxQuantity || 0,
        sold: event.ticketsSold || 0,
        waitlistCount: event.waitlistCount || 0,
        soldOut: event.soldOut || false,
        slug: '...',
        tickets: {
          earlyFemale: { price: 0, displayPrice: 0, sold: 0 },
          earlyMale: { price: 0, displayPrice: 0, sold: 0 },
          generalFemale: { price: 0, displayPrice: 0, sold: 0 },
          generalMale: { price: 0, displayPrice: 0, sold: 0 },
          vip: { price: 0, displayPrice: 0, sold: 0 },
          comp: { sold: 0 }
        },
        revenue: 0, // Will be calculated when details load (base + fees)
        baseRevenue: (event.salesAmount?.monetaryAmount || 0) / 100, // Organizer's revenue
        allInRevenue: 0, // Will be calculated when details load (includes taxes)
        tierSales: {},
        dailySales: {},
        dailyRevenue: {},
        dailyBaseRevenue: {},
        dailyAllInRevenue: {},
        tierRevenue: {},
        tierBaseRevenue: {},
        tierAllInRevenue: {},
        tierAvailability: {},
        rates: [],
        timeSlotReport: null
      }));
    
    return processedEvents;
  }, []);

  // Process a single past event from SQLite archive into dashboard format
  const processPastEventDetail = useCallback((pastEvent) => {
    const detail = pastEvent.detail_data;
    if (!detail) {
      // No detail data - return header-only format
      const isCrossover = pastEvent.source === 'universe+tixr';
      const tixrSold = isCrossover ? (pastEvent.tixr_sold || 0) : 0;
      const tixrRev = isCrossover ? (pastEvent.tixr_revenue || 0) : 0;
      return {
        id: pastEvent.id,
        title: pastEvent.title,
        date: pastEvent.date || 'Unknown',
        timeslotId: pastEvent.timeslot_id,
        isPastEvent: true,
        hasOccupancy: pastEvent.has_occupancy || false,
        isHeaderOnly: true,
        capacity: pastEvent.capacity || 0,
        sold: (pastEvent.sold || 0) + tixrSold,
        waitlistCount: pastEvent.waitlist_count || 0,
        soldOut: pastEvent.sold_out,
        slug: pastEvent.slug || '',
        baseRevenue: (pastEvent.base_revenue || 0) + tixrRev,
        revenue: tixrRev,
        allInRevenue: tixrRev,
        tickets: {
          earlyFemale: { price: 0, displayPrice: 0, sold: 0 },
          earlyMale: { price: 0, displayPrice: 0, sold: 0 },
          rsvpEarlyFemale: { sold: 0, available: 0 },
          rsvpEarlyMale: { sold: 0, available: 0 },
          generalFemale: { price: 0, displayPrice: 0, sold: 0 },
          generalMale: { price: 0, displayPrice: 0, sold: 0 },
          vip: { price: 0, displayPrice: 0, sold: 0 },
          comp: { sold: 0 }
        },
        tierSales: {},
        dailySales: {},
        dailyRevenue: {},
        dailyBaseRevenue: {},
        dailyAllInRevenue: {},
        tierRevenue: {},
        tierBaseRevenue: {},
        tierAllInRevenue: {},
        tierAvailability: {},
        rates: [],
        timeSlotReport: null,
        archivedAt: pastEvent.archived_at,
        source: pastEvent.source || 'universe',
        flyerUrl: pastEvent.flyer_url || null,
        tixrSold: pastEvent.tixr_sold || 0,
        tixrRevenue: pastEvent.tixr_revenue || 0
      };
    }

    // Process full detail data (same logic as processEventDetailData but returns instead of setState)
    const rates = detail.rates?.nodes || [];
    const availability = detail.timeSlots?.nodes?.[0]?.ratesAvailability || [];
    const rateNames = rates.reduce((acc, rate) => {
      acc[rate.id] = rate.name;
      return acc;
    }, {});

    const rawTicketsByDate = detail.timeSlots?.nodes?.[0]?.timeSlotReport?.ticketsByDate || [];
    const rawSalesByDate = detail.timeSlots?.nodes?.[0]?.timeSlotReport?.salesByDate || [];

    const ticketsByDate = Array.isArray(rawTicketsByDate) && rawTicketsByDate[0]?.date
      ? normalizeTicketsByDate(rawTicketsByDate)
      : rawTicketsByDate;
    const salesByDate = Array.isArray(rawSalesByDate) && rawSalesByDate[0]?.date
      ? normalizeSalesByDate(rawSalesByDate)
      : rawSalesByDate;

    const tierSales = {};
    const dailySales = {};
    const dailyRevenue = {};
    const dailyBaseRevenue = {};
    const dailyAllInRevenue = {};
    const tierRevenue = {};
    const tierBaseRevenue = {};
    const tierAllInRevenue = {};

    rates.forEach(rate => {
      const tierName = rate.name;
      const rateAvailability = availability.find(a => a.rateId === rate.id);
      const availableCount = rateAvailability?.countAvailable ?? -1;
      tierSales[tierName] = {
        sold: 0,
        dates: [],
        sortIndex: rate.sortIndex || 999,
        available: availableCount,
        maxQuantity: rate.maxQuantity || 0,
        isAvailable: availableCount === -1 || availableCount > 0,
        displayPrice: rate.displayPrice,
        price: parsePrice(rate.price)
      };
      tierRevenue[tierName] = 0;
      tierBaseRevenue[tierName] = 0;
      tierAllInRevenue[tierName] = 0;
    });

    ticketsByDate.forEach(dayData => {
      if (!dayData || !dayData.day) return;
      dailySales[dayData.day] = 0;
      if (dayData.data && Array.isArray(dayData.data)) {
        dayData.data.forEach(rateData => {
          const tierName = rateNames[rateData.rateId];
          if (tierName && tierSales[tierName]) {
            tierSales[tierName].sold += rateData.value;
            tierSales[tierName].dates.push({ date: dayData.day, count: rateData.value });
            dailySales[dayData.day] += rateData.value;
          }
        });
      }
    });

    salesByDate.forEach(dayData => {
      if (!dayData || !dayData.day) return;
      dailyRevenue[dayData.day] = 0;
      dailyBaseRevenue[dayData.day] = 0;
      dailyAllInRevenue[dayData.day] = 0;
      if (dayData.data && Array.isArray(dayData.data)) {
        dayData.data.forEach(rateData => {
          const tierName = rateNames[rateData.rateId];
          if (tierName) {
            const revenue = rateData.value;
            const baseRevenue = rateData.baseValue || rateData.value;
            const allInRevenue = rateData.allInValue || revenue;
            if (tierRevenue[tierName] !== undefined) {
              tierRevenue[tierName] += revenue;
              tierBaseRevenue[tierName] += baseRevenue;
              tierAllInRevenue[tierName] += allInRevenue;
            }
            dailyRevenue[dayData.day] += revenue;
            dailyBaseRevenue[dayData.day] += baseRevenue;
            dailyAllInRevenue[dayData.day] += allInRevenue;
          }
        });
      }
    });

    // Crossover rows (universe+tixr) carry the Tixr breakdown as detail.tixr
    // (backfilled). Fold its tiers/days into the maps BEFORE the gender bucket
    // loop below so Sales Breakdown / by Tier / by Day / Ratio see both
    // sources. Header sold/revenue keep the tixr_sold/tixr_revenue add-in
    // (Universe's ticketItemsSold excludes Tixr) — no double count.
    const allRates = detail.tixr
      ? mergeTixrDetail({ rates, tierSales, dailySales, dailyRevenue, dailyBaseRevenue, dailyAllInRevenue, tierRevenue, tierBaseRevenue, tierAllInRevenue }, detail.tixr).rates
      : rates;

    // Build tickets structure
    const tickets = {
      earlyFemale: { price: 0, displayPrice: 0, sold: 0 },
      earlyMale: { price: 0, displayPrice: 0, sold: 0 },
      rsvpEarlyFemale: { sold: 0, available: 0 },
      rsvpEarlyMale: { sold: 0, available: 0 },
      generalFemale: { price: 0, displayPrice: 0, sold: 0 },
      generalMale: { price: 0, displayPrice: 0, sold: 0 },
      vip: { price: 0, displayPrice: 0, sold: 0 },
      comp: { sold: 0 }
    };

    const sortedRates = [...allRates].sort((a, b) => a.price - b.price);
    sortedRates.forEach(rate => {
      const tierData = tierSales[rate.name] || { sold: 0, maxQuantity: 0 };
      const name = normalizeTierName(rate.name).toLowerCase();
      const price = rate.price;
      const displayPrice = rate.displayPrice;
      const sold = tierData.sold || 0;
      const available = tierData.available >= 0 ? tierData.available : (tierData.maxQuantity > 0 ? tierData.maxQuantity - tierData.sold : 0);
      // Only ACTIVE tiers set category prices (hidden paid twins poison them).
      // This is the PAST-event processor: the price shown is the cheapest
      // ticket anyone actually bought, so tiers with no sales never set it
      // (Universe "Unleashed" $25/$40 shells with 0 sold were winning over
      // the tiers that sold on crossover nights).
      const canSetPrice = (!rate.state || rate.state === 'ACTIVE') && sold > 0;

      if (name.includes('rsvp') && name.includes('early') && name.includes('female')) {
        tickets.rsvpEarlyFemale.sold += sold;
        tickets.rsvpEarlyFemale.available += available;
      } else if (name.includes('rsvp') && name.includes('early') && name.includes('male')) {
        tickets.rsvpEarlyMale.sold += sold;
        tickets.rsvpEarlyMale.available += available;
      } else if (name.includes('early arrival') && name.includes('female')) {
        if (canSetPrice && (price > 0 || tickets.earlyFemale.price === 0)) {
          tickets.earlyFemale.price = price;
          tickets.earlyFemale.displayPrice = displayPrice;
        }
        tickets.earlyFemale.sold += sold;
      } else if (name.includes('early arrival') && name.includes('male')) {
        if (canSetPrice && (price > 0 || tickets.earlyMale.price === 0)) {
          tickets.earlyMale.price = price;
          tickets.earlyMale.displayPrice = displayPrice;
        }
        tickets.earlyMale.sold += sold;
      } else if (name.includes('general admission') && name.includes('female') && !name.includes('early')) {
        const hasCapacity = tierData.maxQuantity === 0 || tierData.sold < tierData.maxQuantity;
        if (canSetPrice && hasCapacity && (tickets.generalFemale.price === 0 || price < tickets.generalFemale.price)) {
          tickets.generalFemale.price = price;
          tickets.generalFemale.displayPrice = displayPrice;
        }
        tickets.generalFemale.sold += sold;
      } else if (name.includes('general admission') && name.includes('male') && !name.includes('early')) {
        const hasCapacity = tierData.maxQuantity === 0 || tierData.sold < tierData.maxQuantity;
        if (canSetPrice && hasCapacity && (tickets.generalMale.price === 0 || price < tickets.generalMale.price)) {
          tickets.generalMale.price = price;
          tickets.generalMale.displayPrice = displayPrice;
        }
        tickets.generalMale.sold += sold;
      } else if (name.includes('vip')) {
        tickets.vip = { price: canSetPrice || !tickets.vip.price ? price : tickets.vip.price, displayPrice: canSetPrice || !tickets.vip.price ? displayPrice : tickets.vip.displayPrice, sold };
      }
    });

    Object.entries(tierSales).forEach(([tierName, tierData]) => {
      if (tierName.toLowerCase().includes('comp')) {
        tickets.comp.sold += tierData.sold || 0;
      }
    });

    const newSoldCount = detail.timeSlots?.nodes?.[0]?.timeSlotReport?.ticketItemsSold || 0;
    // Only add Tixr numbers for crossover events — Tixr-only events already have Tixr data in detail_data.
    // tixr_revenue is what buyers PAID (fees included). With the backfilled
    // block we can split it: base = paid − fees (Tixr's face value, the
    // counterpart of Universe totalGross); Revenue / All-in get the paid amount.
    const isCrossover = pastEvent.source === 'universe+tixr';
    const tixrSold = isCrossover ? (pastEvent.tixr_sold || 0) : 0;
    const tixrPaid = isCrossover ? (pastEvent.tixr_revenue || 0) : 0;
    const tixrBase = isCrossover && detail.tixr && Number.isFinite(Number(detail.tixr.totalBase))
      ? Number(detail.tixr.totalBase)
      : tixrPaid;

    return {
      id: pastEvent.id,
      title: pastEvent.title || detail.title,
      date: pastEvent.date || 'Unknown',
      timeslotId: pastEvent.timeslot_id,
      isPastEvent: true,
      hasOccupancy: pastEvent.has_occupancy || false,
      isHeaderOnly: false,
      hasLoadedDetails: true,
      capacity: detail.capacity || pastEvent.capacity || 0,
      sold: (newSoldCount || pastEvent.sold || 0) + tixrSold,
      waitlistCount: detail.waitlistCount || pastEvent.waitlist_count || 0,
      soldOut: pastEvent.sold_out,
      slug: detail.slugParam || pastEvent.slug || '',
      revenue: (detail.timeSlots?.nodes?.[0]?.timeSlotReport?.totalGross || 0) + (detail.timeSlots?.nodes?.[0]?.timeSlotReport?.totalFee || 0) + tixrPaid,
      baseRevenue: (detail.timeSlots?.nodes?.[0]?.timeSlotReport?.totalGross || pastEvent.base_revenue || 0) + tixrBase,
      allInRevenue: (detail.timeSlots?.nodes?.[0]?.timeSlotReport?.totalNet || 0) + tixrPaid,
      tickets,
      tierSales,
      dailySales,
      dailyRevenue,
      dailyBaseRevenue,
      dailyAllInRevenue,
      tierRevenue,
      tierBaseRevenue,
      tierAllInRevenue,
      tierAvailability: availability,
      rates: allRates,
      timeSlotReport: detail.timeSlots?.nodes?.[0]?.timeSlotReport || null,

      archivedAt: pastEvent.archived_at,
      source: pastEvent.source || 'universe',
      flyerUrl: pastEvent.flyer_url || null,
      tixrSold: pastEvent.tixr_sold || 0,
      tixrRevenue: pastEvent.tixr_revenue || 0
    };
  }, []);

  // Fetch a page of past events from the archive (server-side
  // paginate/sort/search; header-only rows, detail loads on expand).
  // reset=true REPLACES the list (tab open, or a new sort/search); reset=false
  // APPENDS (infinite scroll). The response's `total` is authoritative for the
  // tab count and hasMore.
  const loadPastEvents = useCallback(async ({ reset = true } = {}) => {
    const q = searchTerm.trim();
    const sig = `${sortBy}|${sortDir}|${q}`;
    pastFetchSigRef.current = sig;
    const offset = reset ? 0 : pastLoadedCountRef.current;
    if (reset) setPastEventsLoading(true); else setPastLoadingMore(true);
    try {
      const { data, total, occupancySupported: occSupported } = await fetchPastEvents({
        limit: PAST_PAGE_SIZE, offset, q, sort: sortBy, dir: sortDir,
      });
      // Stale-response guard: a newer sort/search has superseded this fetch.
      if (pastFetchSigRef.current !== sig) return;
      setOccupancySupported(occSupported);
      const processed = [];
      data.forEach(pe => {
        try { processed.push(processPastEventDetail(pe)); }
        catch (err) { console.error(`Error processing past event ${pe.id} "${pe.title}":`, err.message); }
      });
      setPastEvents(prev => {
        const next = reset ? processed : [...prev, ...processed];
        pastLoadedCountRef.current = next.length;
        pastEventsRef.current = next;
        setPastHasMore(next.length < total);
        return next;
      });
      setPastTotal(total);
      setPastEventsLoaded(true);
      console.log(`📦 Past events: +${processed.length} (${reset ? 'page 1' : 'more'}), total ${total}`);
    } catch (err) {
      console.error('Error loading past events:', err);
    } finally {
      if (reset) setPastEventsLoading(false); else setPastLoadingMore(false);
    }
  }, [sortBy, sortDir, searchTerm, processPastEventDetail]);

  // Append the next page (infinite scroll).
  const loadMorePastEvents = useCallback(() => {
    if (pastEventsLoading || pastLoadingMore || !pastHasMore) return;
    loadPastEvents({ reset: false });
  }, [pastEventsLoading, pastLoadingMore, pastHasMore, loadPastEvents]);

  // Infinite-scroll: when the bottom sentinel enters view (and there's more to
  // load), append the next page. Only active while the past archive is shown.
  useEffect(() => {
    const active = (activeTab === 'past' || searchTerm.trim().length > 0) && pastHasMore;
    const el = pastSentinelRef.current;
    if (!active || !el) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) loadMorePastEvents(); },
      { root: null, rootMargin: '300px', threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [activeTab, searchTerm, pastHasMore, loadMorePastEvents]);

  // Fetch the past total once on mount (count-only, no rows/detail) so the
  // "Past (N)" tab label shows the true count before the tab is ever opened.
  useEffect(() => {
    let cancelled = false;
    fetchPastEvents({ limit: 0 })
      .then(({ total }) => { if (!cancelled) setPastTotal(total); })
      .catch(err => console.error('Error fetching past count:', err.message));
    return () => { cancelled = true; };
  }, []);

  // Re-fetch page 1 when sort/dir or search changes while the past archive is in
  // view (past tab, or cross-tab search). Debounced so each keystroke doesn't
  // fire a query. Replaces the list (offset resets to 0); scroll appends carry
  // the same params.
  useEffect(() => {
    const active = activeTab === 'past' || searchTerm.trim().length > 0;
    if (!active) return;
    const t = setTimeout(() => { loadPastEvents({ reset: true }); }, 250);
    return () => clearTimeout(t);
  }, [sortBy, sortDir, searchTerm, activeTab, loadPastEvents]);

  // Load event headers - exactly as before
  const loadEventHeadersHTTP = useCallback(async () => {
    console.log('📡 Loading events via HTTP');
    const { events: upcomingEvents, cacheStatus: listCacheStatus } = await fetchUpcomingEvents();

    setCacheStatus(listCacheStatus);
    const processedEvents = processEventsData(upcomingEvents);

    // Merge with existing events to preserve loaded details.
    // Use functional setEvents so the merge reads the LATEST committed state,
    // not a stale eventsRef that may not have caught up to a pending detail update.
    let merged;
    setEvents(prevEvents => {
      const existingEventsMap = new Map(prevEvents.map(e => [e.id, e]));
      merged = processedEvents.map(newEvent => {
        const existing = existingEventsMap.get(newEvent.id);
        if (existing && !existing.isHeaderOnly) {
          return existing;
        }
        return newEvent;
      });
      eventsRef.current = merged;
      return merged;
    });

    return merged;
  }, [processEventsData]);

  // Optimized event header loading with subscription logic - exactly as before
  const loadEventHeaders = useCallback(async () => {
    console.log('📡 Loading events list via HTTP (optimized hybrid approach)');
    return await loadEventHeadersHTTP();
  }, [loadEventHeadersHTTP]);

  // (The old duplicate mount-fetch effect lived here — initialize() below is
  // the single initial loader now; two concurrent list fetches raced the merge.)

  // Auto-retry a failed initial load. Without this, a cold-cache/timeout
  // failure stranded an EMPTY dashboard indefinitely (error state existed but
  // nothing rendered it and nothing retried — the "just sits there" bug).
  useEffect(() => {
    if (!error || events.length > 0) return;
    const t = setInterval(() => {
      console.log('🔁 Auto-retrying initial event load...');
      loadEventHeaders().then(() => setError(null)).catch(() => {});
    }, 20000);
    return () => clearInterval(t);
  }, [error, events.length, loadEventHeaders]);

  // Helper function to normalize ticketsByDate from new format to old format
  const normalizeTicketsByDate = (tickets) => {
    const grouped = {};
    tickets.forEach(item => {
      if (!grouped[item.date]) {
        grouped[item.date] = { day: item.date, data: [] };
      }
      grouped[item.date].data.push({
        rateId: item.rate_id,
        value: item.total
      });
    });
    return Object.values(grouped);
  };
  
  // Helper function to normalize salesByDate from new format to old format
  const normalizeSalesByDate = (sales) => {
    const grouped = {};
    sales.forEach(item => {
      if (!grouped[item.date]) {
        grouped[item.date] = { day: item.date, data: [] };
      }
      grouped[item.date].data.push({
        rateId: item.rate_id,
        value: item.total_amount_net,
        baseValue: item.total_fulfillment_amount,
        allInValue: item.total_amount_with_tax || item.total_amount_net // Use with tax if available
      });
    });
    return Object.values(grouped);
  };

  // Stable content hash of the price-sensitive fields. Same prices + same sold
  // counts => same hash regardless of timestamps/metadata.
  const hashTicketContent = (detail) => {
    const rates = (detail?.rates?.nodes || []).map(r => `${r.id}:${r.price}:${r.displayPrice}:${r.maxQuantity}:${r.state || ''}`).join('|');
    const report = detail?.timeSlots?.nodes?.[0]?.timeSlotReport;
    const totals = report ? `${report.ticketItemsSold}:${report.totalGross}:${report.totalNet}` : '';
    return `${rates}#${totals}`;
  };

  // Helper function to process event detail data
  const processEventDetailData = useCallback((eventId, detail) => {
    // Drop stale updates: if we've already applied a newer payload (REST or WS),
    // ignore this one. This prevents flicker when REST and WS arrive out of order.
    let timestampAdvanced = false;
    if (detail?._updatedAt) {
      const incomingMs = new Date(detail._updatedAt).getTime();
      const lastMs = lastEventUpdateRef.current.get(eventId);
      if (lastMs && incomingMs < lastMs) {
        console.log(`⏭ Skipping stale update for ${eventId} (incoming ${detail._updatedAt} < last ${new Date(lastMs).toISOString()})`);
        return;
      }
      timestampAdvanced = !lastMs || incomingMs > lastMs;
      lastEventUpdateRef.current.set(eventId, incomingMs);
    }

    // Defense in depth: ring-buffer of recent content hashes. If incoming content
    // matches a hash we already moved past (not the current one), drop it - it's
    // stale data that somehow got a fresh timestamp. EXCEPT when the timestamp
    // genuinely advanced: a refund that returns counts to an exact earlier state
    // hashes identically to that old state — dropping it froze the card at the
    // pre-refund number until the next unrelated sale.
    const incomingHash = hashTicketContent(detail);
    const ring = lastEventHashRingRef.current.get(eventId) || [];
    const lastHash = ring[ring.length - 1];
    if (!timestampAdvanced && lastHash && lastHash !== incomingHash && ring.includes(incomingHash)) {
      console.log(`⏭ Skipping stale-by-hash update for ${eventId} (hash matches earlier, not current)`);
      return;
    }
    if (lastHash !== incomingHash) {
      ring.push(incomingHash);
      if (ring.length > 5) ring.shift();
      lastEventHashRingRef.current.set(eventId, ring);
    }

    // Validate that the detail data matches the expected event ID
    if (detail.id && detail.id !== eventId) {
      console.error(`❌ Event ID mismatch! Expected ${eventId} but received detail for ${detail.id}`);
      console.error(`   Expected event: ${eventsRef.current.find(e => e.id === eventId)?.title || 'Unknown'}`);
      console.error(`   Received event: ${detail.title || 'Unknown'}`);
      
      // Unsubscribe from the broken WebSocket subscription
      console.log(`🔧 Attempting to recover from mismatch by unsubscribing from WebSocket`);
      const unsubscribe = eventSubscriptionsRef.current.get(eventId);
      if (unsubscribe) {
        unsubscribe();
        eventSubscriptionsRef.current.delete(eventId);
        setSubscribedEventIds(prev => {
          if (!prev.has(eventId)) return prev;
          const next = new Set(prev);
          next.delete(eventId);
          return next;
        });
      }
      
      // Mark event as needing HTTP reload
      console.log(`🔄 Event ${eventId} will be reloaded via HTTP on next attempt`);
      return; // Don't process mismatched data
    }
    
    // Debug logging for all events
    console.log(`🎯 Processing event detail for ${eventId}:`, detail.title || 'Unknown');
    console.log('   Has timeSlots:', !!detail.timeSlots);
    console.log('   TimeSlot nodes:', detail.timeSlots?.nodes?.length || 0);
    if (detail.timeSlots?.nodes?.[0]) {
      const ts = detail.timeSlots.nodes[0];
      console.log('   TimeSlot ID:', ts.id);
      console.log('   Has timeSlotReport:', !!ts.timeSlotReport);
      if (ts.timeSlotReport) {
        console.log('   Tickets sold:', ts.timeSlotReport.ticketItemsSold);
        console.log('   Total gross:', ts.timeSlotReport.totalGross);
      }
    }

    // Guarded: a malformed WS payload (missing rates/timeSlots) must fail this
    // one update, not throw inside the socket handler and kill the subscription.
    const rates = detail.rates?.nodes || [];
    const availability = detail.timeSlots?.nodes?.[0]?.ratesAvailability || [];
    const rateNames = rates.reduce((acc, rate) => {
      acc[rate.id] = rate.name;
      return acc;
    }, {});
    
    const rawTicketsByDate = detail.timeSlots?.nodes?.[0]?.timeSlotReport?.ticketsByDate || [];
    const rawSalesByDate = detail.timeSlots?.nodes?.[0]?.timeSlotReport?.salesByDate || [];
    
    // Normalize data structure - handle both old and new formats
    const ticketsByDate = Array.isArray(rawTicketsByDate) && rawTicketsByDate[0]?.date 
      ? normalizeTicketsByDate(rawTicketsByDate) 
      : rawTicketsByDate;
    const salesByDate = Array.isArray(rawSalesByDate) && rawSalesByDate[0]?.date
      ? normalizeSalesByDate(rawSalesByDate)
      : rawSalesByDate;
    
    // Initialize all tracking objects
    const tierSales = {};
    const dailySales = {};
    const dailyRevenue = {};
    const dailyBaseRevenue = {};
    const dailyAllInRevenue = {};
    const tierRevenue = {};
    const tierBaseRevenue = {};
    const tierAllInRevenue = {};
    
    // Process rates
    rates.forEach(rate => {
      const tierName = rate.name;
      const rateAvailability = availability.find(a => a.rateId === rate.id);
      const availableCount = rateAvailability?.countAvailable ?? -1;
      
      tierSales[tierName] = {
        sold: 0,
        dates: [],
        sortIndex: rate.sortIndex || 999,
        available: availableCount,
        maxQuantity: rate.maxQuantity || 0,
        isAvailable: availableCount === -1 || availableCount > 0,
        displayPrice: rate.displayPrice,
        price: parsePrice(rate.price)
      };
      tierRevenue[tierName] = 0;
      tierBaseRevenue[tierName] = 0;
      tierAllInRevenue[tierName] = 0;
    });
    
    // Process tickets by date
    ticketsByDate.forEach(dayData => {
      if (!dayData || !dayData.day) return;
      
      dailySales[dayData.day] = 0;
      
      if (dayData.data && Array.isArray(dayData.data)) {
        dayData.data.forEach(rateData => {
          const tierName = rateNames[rateData.rateId];
          if (tierName && tierSales[tierName]) {
            tierSales[tierName].sold += rateData.value;
            tierSales[tierName].dates.push({
              date: dayData.day,
              count: rateData.value
            });
            dailySales[dayData.day] += rateData.value;
          }
        });
      }
    });
    
    // Process sales by date
    salesByDate.forEach(dayData => {
      if (!dayData || !dayData.day) return;
      
      dailyRevenue[dayData.day] = 0;
      dailyBaseRevenue[dayData.day] = 0;
      dailyAllInRevenue[dayData.day] = 0;
      
      if (dayData.data && Array.isArray(dayData.data)) {
        dayData.data.forEach(rateData => {
          const tierName = rateNames[rateData.rateId];
          if (tierName) {
            const revenue = rateData.value; // total_amount_net (includes fees)
            const baseRevenue = rateData.baseValue || rateData.value; // total_fulfillment_amount (base, no fees)
            const allInRevenue = rateData.allInValue || revenue; // total with tax if available
            
            if (tierRevenue[tierName] !== undefined) {
              tierRevenue[tierName] += revenue;
              tierBaseRevenue[tierName] += baseRevenue;
              tierAllInRevenue[tierName] += allInRevenue;
            }
            
            dailyRevenue[dayData.day] += revenue;
            dailyBaseRevenue[dayData.day] += baseRevenue;
            dailyAllInRevenue[dayData.day] += allInRevenue;
          }
        });
      }
    });
    
    // Get lowest price for display
    
    // Populate tickets structure from tier data for backward compatibility
    const tickets = {
      earlyFemale: { price: 0, displayPrice: 0, sold: 0 },
      earlyMale: { price: 0, displayPrice: 0, sold: 0 },
      rsvpEarlyFemale: { sold: 0, available: 0 },
      rsvpEarlyMale: { sold: 0, available: 0 },
      generalFemale: { price: 0, displayPrice: 0, sold: 0 },
      generalMale: { price: 0, displayPrice: 0, sold: 0 },
      vip: { price: 0, displayPrice: 0, sold: 0 },
      comp: { sold: 0 }
    };

    // Map tier data to ticket categories
    // First, sort rates by price to ensure we get the lowest available tier
    const sortedRates = [...rates].sort((a, b) => a.price - b.price);

    // Classify each rate into a classic bucket via the per-tenant ticket model
    // (classifyRate), then accumulate price/sold per bucket. The matcher is
    // model-driven; the LIV default reproduces the source's exact keyword
    // chain, so output is unchanged. Per-bucket accumulation is preserved
    // verbatim from the source.
    sortedRates.forEach(rate => {
      const tierData = tierSales[rate.name] || { sold: 0, maxQuantity: 0 };
      const name = normalizeTierName(rate.name).toLowerCase();
      const price = rate.price;
      const displayPrice = rate.displayPrice;
      const sold = tierData.sold || 0;
      const available = tierData.available >= 0 ? tierData.available : (tierData.maxQuantity > 0 ? tierData.maxQuantity - tierData.sold : 0);
      // Only ACTIVE tiers may SET a category's price: hidden/inactive paid twins
      // (e.g. "Early Arrival - Male (Hidden)" $40 beside the free $0 ACTIVE tier)
      // otherwise poison the category price and misprice free-claim estimates.
      // Sold still accumulates from every state. Missing state = legacy = active.
      const canSetPrice = !rate.state || rate.state === 'ACTIVE';

      switch (classifyRate(name)) {
        // RSVP early arrival (free) — separate from paid early arrival
        case 'rsvpEarlyFemale':
          tickets.rsvpEarlyFemale.sold += sold;
          tickets.rsvpEarlyFemale.available += available;
          break;
        case 'rsvpEarlyMale':
          tickets.rsvpEarlyMale.sold += sold;
          tickets.rsvpEarlyMale.available += available;
          break;
        // Paid early arrival
        case 'earlyFemale':
          if (canSetPrice && (price > 0 || tickets.earlyFemale.price === 0)) {
            tickets.earlyFemale.price = price;
            tickets.earlyFemale.displayPrice = displayPrice;
          }
          tickets.earlyFemale.sold += sold;
          break;
        case 'earlyMale':
          if (canSetPrice && (price > 0 || tickets.earlyMale.price === 0)) {
            tickets.earlyMale.price = price;
            tickets.earlyMale.displayPrice = displayPrice;
          }
          tickets.earlyMale.sold += sold;
          break;
        // General admission — track the lowest tier that still has capacity
        case 'generalFemale': {
          const hasCapacity = tierData.maxQuantity === 0 || tierData.sold < tierData.maxQuantity;
          if (canSetPrice && hasCapacity && (tickets.generalFemale.price === 0 || price < tickets.generalFemale.price)) {
            tickets.generalFemale.price = price;
            tickets.generalFemale.displayPrice = displayPrice;
          }
          tickets.generalFemale.sold += sold;
          break;
        }
        case 'generalMale': {
          const hasCapacity = tierData.maxQuantity === 0 || tierData.sold < tierData.maxQuantity;
          if (canSetPrice && hasCapacity && (tickets.generalMale.price === 0 || price < tickets.generalMale.price)) {
            tickets.generalMale.price = price;
            tickets.generalMale.displayPrice = displayPrice;
          }
          tickets.generalMale.sold += sold;
          break;
        }
        case 'vip':
          if (canSetPrice || tickets.vip.price === 0) {
            tickets.vip.price = price;
            tickets.vip.displayPrice = displayPrice;
          }
          tickets.vip.sold = sold;
          break;
        // Only reachable via an explicit match override (drag-assigned in the
        // config editor) — keyword comps classify null and are counted below.
        case 'comp':
          tickets.comp.sold += sold;
          break;
        default:
          break;
      }
    });

    // Handle comp tickets separately if they exist in tierSales but not in rates.
    // Skip ONLY tiers a match override re-assigned elsewhere (the switch above
    // already accumulated those); keyword behavior stays exactly as the source.
    Object.entries(tierSales).forEach(([tierName, tierData]) => {
      const norm = normalizeTierName(tierName).toLowerCase();
      if (matchOverrideKey(norm) !== null) return;
      if (isComp(tierName.toLowerCase())) {
        tickets.comp.sold += tierData.sold || 0;
      }
    });
    
    // Get new sold count from the update
    const newSoldCount = detail.timeSlots.nodes[0]?.timeSlotReport?.ticketItemsSold || 0;
    
    // Check for ticket changes before updating state
    const currentEvent = eventsRef.current.find(e => e.id === eventId);
    const detailLoads = (currentEvent?.detailLoadCount || 0);
    const hasTicketChange = currentEvent && detailLoads >= 1 && currentEvent.sold !== newSoldCount && currentEvent.sold >= 0;
    const ticketDiff = hasTicketChange ? newSoldCount - currentEvent.sold : 0;
    
    // Track category-specific changes (adaptive to ticket structure)
    let categoryDiffs = {};
    if (currentEvent && detailLoads >= 1 && currentEvent.tickets) {
      // Keep hardcoded categories for backward compatibility
      categoryDiffs = {
        earlyFemale: tickets.earlyFemale.sold - (currentEvent.tickets.earlyFemale?.sold || 0),
        earlyMale: tickets.earlyMale.sold - (currentEvent.tickets.earlyMale?.sold || 0),
        generalFemale: tickets.generalFemale.sold - (currentEvent.tickets.generalFemale?.sold || 0),
        generalMale: tickets.generalMale.sold - (currentEvent.tickets.generalMale?.sold || 0),
        vip: tickets.vip.sold - (currentEvent.tickets.vip?.sold || 0),
        comp: tickets.comp.sold - (currentEvent.tickets.comp?.sold || 0),
        // Free RSVP tiers - count as sales for the +N animation purposes
        rsvpEarlyFemale: (tickets.rsvpEarlyFemale?.sold || 0) - (currentEvent.tickets.rsvpEarlyFemale?.sold || 0),
        rsvpEarlyMale: (tickets.rsvpEarlyMale?.sold || 0) - (currentEvent.tickets.rsvpEarlyMale?.sold || 0),
        // Totals roll up RSVP + paid Early + GA, so totals also animate on RSVP signups
        totalFemale: ((tickets.rsvpEarlyFemale?.sold || 0) + tickets.earlyFemale.sold + tickets.generalFemale.sold) -
                     ((currentEvent.tickets.rsvpEarlyFemale?.sold || 0) + (currentEvent.tickets.earlyFemale?.sold || 0) + (currentEvent.tickets.generalFemale?.sold || 0)),
        totalMale: ((tickets.rsvpEarlyMale?.sold || 0) + tickets.earlyMale.sold + tickets.generalMale.sold) -
                   ((currentEvent.tickets.rsvpEarlyMale?.sold || 0) + (currentEvent.tickets.earlyMale?.sold || 0) + (currentEvent.tickets.generalMale?.sold || 0)),
        totalEarly: ((tickets.rsvpEarlyFemale?.sold || 0) + (tickets.rsvpEarlyMale?.sold || 0) + tickets.earlyFemale.sold + tickets.earlyMale.sold) -
                    ((currentEvent.tickets.rsvpEarlyFemale?.sold || 0) + (currentEvent.tickets.rsvpEarlyMale?.sold || 0) + (currentEvent.tickets.earlyFemale?.sold || 0) + (currentEvent.tickets.earlyMale?.sold || 0))
      };

      // Add dynamic tier changes for ALL tiers (adaptive approach)
      if (tierSales && currentEvent.tierSales) {
        Object.keys(tierSales).forEach(tierName => {
          const currentTierSold = tierSales[tierName]?.sold || 0;
          const previousTierSold = currentEvent.tierSales[tierName]?.sold || 0;
          const diff = currentTierSold - previousTierSold;

          // Only track tiers that have changes
          if (diff !== 0) {
            categoryDiffs[`tier_${tierName}`] = diff;
          }
        });
      }
    }
    
    // Handle ticket state-change notifications (dispute / void / refund).
    // Universe sends these as ticket_update webhooks; the proxy detects them and
    // annotates `_ticketStateChange` on the event payload. These should NOT fire
    // a sale alert - they're financial losses, not sales. Take this branch first
    // and skip the sale-detection block below.
    if (detail._ticketStateChange) {
      const sc = detail._ticketStateChange;
      const parts = [];
      if (sc.counts?.disputed) parts.push(`${sc.counts.disputed} disputed`);
      if (sc.counts?.voided)   parts.push(`${sc.counts.voided} voided`);
      if (sc.counts?.refunded) parts.push(`${sc.counts.refunded} refunded`);
      const summary = parts.join(', ');
      console.log(`⚠️ Ticket state change for "${currentEvent?.title || sc.eventTitle}": ${summary}`);
      const note = {
        id: `${eventId}-statechange-${Date.now()}`,
        timestamp: new Date().toISOString(),
        eventId,
        eventTitle: currentEvent?.title || sc.eventTitle || 'Event',
        type: 'state_change',
        counts: sc.counts || {},
        summary
      };
      setNotificationHistory(prev => [note, ...prev]);
    }

    // Handle ticket change notifications (outside of setEvents to prevent duplicates).
    // Gated by !detail._ticketStateChange so a single webhook with both a sold-count
    // change and a dispute doesn't double-fire.
    if (!detail._ticketStateChange && hasTicketChange && ticketDiff > 0) {
      console.log(`🔔 Ticket Sale Alert: "${currentEvent.title}" +${ticketDiff} ticket${ticketDiff > 1 ? 's' : ''} sold!`);
      playNotificationSound();

      // Calculate revenue for this sale
      // Check if we have actual transaction data from backend (via WebSocket)
      let saleRevenue = 0;
      let isEstimate = true;

      const newGrossReport = detail.timeSlots?.nodes?.[0]?.timeSlotReport;
      const newGross = newGrossReport ? (newGrossReport.totalGross || 0) + (newGrossReport.totalFee || 0) : null;
      if (detail._ticketSale && detail._ticketSale.revenue != null) {
        // Use actual amount from the backend (webhook tx or report gross delta).
        // != null, NOT truthy: $0 is a real amount — free-tier claims.
        saleRevenue = detail._ticketSale.revenue;
        isEstimate = detail._ticketSale.isEstimate || false;
        console.log(`💰 Using ${isEstimate ? 'ESTIMATED' : 'ACTUAL'} revenue from backend: $${saleRevenue.toFixed(2)}`);
      } else if (newGross != null && currentEvent.revenue != null && currentEvent.hasLoadedDetails) {
        // Client-side gross delta: the REAL charged amount for the tickets sold
        // between the previous detail load and this one. Free claims = $0 exactly.
        saleRevenue = Math.max(0, newGross - currentEvent.revenue);
        isEstimate = false;
        console.log(`💰 Using client gross delta: $${saleRevenue.toFixed(2)}`);
      } else {
        // Last resort: per-TIER estimate — each moved tier at its OWN price, so a
        // free tier contributes $0 even when a hidden paid twin shares its
        // category (the "$40 hidden Early tier" bug). Never category-price × diff.
        const priceByTier = {};
        (detail.rates?.nodes || []).forEach((r) => { if (r?.name) priceByTier[r.name] = r.price || 0; });
        if (tierSales && currentEvent.tierSales) {
          Object.keys(tierSales).forEach((tierName) => {
            const diff = (tierSales[tierName]?.sold || 0) - (currentEvent.tierSales[tierName]?.sold || 0);
            if (diff > 0) saleRevenue += diff * (priceByTier[tierName] || 0);
          });
        }
        console.log(`⚠️ No backend transaction data - using per-tier ESTIMATED revenue: $${saleRevenue.toFixed(2)}`);
      }
      
      // Add to notification history
      const notification = {
        id: `${eventId}-${Date.now()}`,
        timestamp: new Date().toISOString(),
        eventId: eventId,
        eventTitle: currentEvent.title,
        type: 'sale',
        ticketsSold: ticketDiff,
        revenue: saleRevenue,
        details: {
          rsvpEarlyFemale: categoryDiffs.rsvpEarlyFemale || 0,
          rsvpEarlyMale: categoryDiffs.rsvpEarlyMale || 0,
          earlyFemale: categoryDiffs.earlyFemale || 0,
          earlyMale: categoryDiffs.earlyMale || 0,
          generalFemale: categoryDiffs.generalFemale || 0,
          generalMale: categoryDiffs.generalMale || 0,
          vip: categoryDiffs.vip || 0,
          comp: categoryDiffs.comp || 0
        },
        prices: {
          earlyFemale: tickets.earlyFemale.price || 0,
          earlyMale: tickets.earlyMale.price || 0,
          generalFemale: tickets.generalFemale.price || 0,
          generalMale: tickets.generalMale.price || 0,
          vip: tickets.vip.price || 0
        }
      };
      
      setNotificationHistory(prev => [notification, ...prev]);

      // NOTE: Push notifications are now sent directly from the backend webhook handler
      // when Universe sends ticket_purchase webhooks. This prevents duplicate notifications
      // from multiple frontend instances. The frontend only maintains notification history for UI.

      // Set animation states
      setRecentlyUpdatedEvents(prev => new Set(prev).add(eventId));
      setTicketChanges(prev => ({
        ...prev,
        [eventId]: ticketDiff
      }));
      
      // Set category-specific changes
      setCategoryChanges(prev => ({
        ...prev,
        [eventId]: categoryDiffs
      }));
      
      // Remove animation after delay
      setTimeout(() => {
        setRecentlyUpdatedEvents(prev => {
          const newSet = new Set(prev);
          newSet.delete(eventId);
          return newSet;
        });
        setTicketChanges(prev => {
          const newChanges = { ...prev };
          delete newChanges[eventId];
          return newChanges;
        });
        setCategoryChanges(prev => {
          const newChanges = { ...prev };
          delete newChanges[eventId];
          return newChanges;
        });
      }, 15000); // Increased to 15 seconds for better visibility
    }
    
    // Update the event data
    setEvents(prevEvents => {
      
      return prevEvents.map(event => {
        if (event.id === eventId) {
          const updatedEvent = {
            ...event,
            slug: detail.slugParam || event.slug,
            capacity: detail.capacity || event.capacity,
            waitlistCount: detail.waitlistCount || 0,
            sold: newSoldCount,
            revenue: (detail.timeSlots.nodes[0]?.timeSlotReport?.totalGross || 0) + (detail.timeSlots.nodes[0]?.timeSlotReport?.totalFee || 0),
            baseRevenue: detail.timeSlots.nodes[0]?.timeSlotReport?.totalGross || 0,
            allInRevenue: detail.timeSlots.nodes[0]?.timeSlotReport?.totalNet || 0,
            tickets,
            tierSales,
            dailySales,
            dailyRevenue,
            dailyBaseRevenue,
            dailyAllInRevenue,
            tierRevenue,
            tierBaseRevenue,
            tierAllInRevenue,
            tierAvailability: availability,
            rates: rates,
            timeSlotReport: detail.timeSlots.nodes[0]?.timeSlotReport || null,

            isHeaderOnly: false,
            hasLoadedDetails: true,
            detailLoadCount: (event.detailLoadCount || 0) + 1
          };
          
          // Update revenue display format (default to base revenue)
          setEventRevenueDisplay(prev => ({
            ...prev,
            [eventId]: prev[eventId] !== undefined ? prev[eventId] : 'revenue'
          }));
          
          // Update price display format
          setEventPriceDisplay(prev => ({
            ...prev,
            [eventId]: prev[eventId] || false
          }));
          
          return updatedEvent;
        }
        return event;
      });
    });
    
    // Update last refreshed time
    setEventLastRefreshed(prev => ({
      ...prev,
      [eventId]: new Date()
    }));
  }, [playNotificationSound, pushEnabled]); // eslint-disable-line react-hooks/exhaustive-deps -- events via eventsRef: a state dep here recreated the callback chain and tore down the IntersectionObserver on every update

  // Load event details via HTTP - exactly as before
  const loadEventDetailsHTTP = useCallback(async (eventId, forceRefresh = false) => {
    console.log(`📡 Loading event details via HTTP: ${eventId}`);
    
    const event = eventsRef.current.find(e => e.id === eventId);
    if (!event || !event.timeslotId) return;

    const detail = await fetchEventDetail(eventId, event.timeslotId, forceRefresh);
    processEventDetailData(eventId, detail);
  }, [processEventDetailData]);

  // Helper: subscribe an already-loaded event to WS for live updates.
  // Used after a successful REST load so the card stays current without a stuck-subscription risk.
  const subscribeEventForUpdates = useCallback((eventId) => {
    const isWebSocketReady = useWebSocketEnabled && wsConnected;
    if (!isWebSocketReady) return;
    if (eventSubscriptionsRef.current.has(eventId)) return;
    const event = eventsRef.current.find(e => e.id === eventId);
    if (!event || !event.timeslotId) return;

    console.log(`📡 Subscribing ${eventId} to WS for live updates`);
    const unsubscribe = subscribeToEventDetail(
      eventId,
      event.timeslotId,
      (data) => {
        if (data?.errors) {
          console.error('WS event detail GraphQL errors:', data.errors);
          return;
        }
        const detail = data?.data?.event;
        if (!detail) return;
        if (data.updatedAt) detail._updatedAt = data.updatedAt;
        processEventDetailData(eventId, detail);
      }
    );
    eventSubscriptionsRef.current.set(eventId, unsubscribe);
    eventsLoadedViaHTTP.current.delete(eventId);
    setSubscribedEventIds(prev => {
      if (prev.has(eventId)) return prev;
      const next = new Set(prev);
      next.add(eventId);
      return next;
    });
  }, [wsConnected, useWebSocketEnabled, subscribeToEventDetail, processEventDetailData]);

  // REST-first event detail loader. Always fetches over HTTP (timeout + retry in api.js).
  // On success: upgrades to WS for live updates. On failure: stores per-event error for the retry UI.
  const loadEventDetails = useCallback(async (eventId, forceRefresh = false) => {
    if (loadingEvents.has(eventId) || loadingQueueRef.current.has(eventId)) {
      return;
    }

    const cachedEvent = eventsRef.current.find(e => e.id === eventId);
    if (!forceRefresh && cachedEvent && cachedEvent.rates && cachedEvent.rates.length > 0 && cachedEvent.hasLoadedDetails) {
      console.log(`📦 Event ${eventId} already has cached details, skipping load`);
      return;
    }

    // Force refresh: drop existing WS subscription before re-fetching
    if (forceRefresh && eventSubscriptionsRef.current.has(eventId)) {
      const unsubscribe = eventSubscriptionsRef.current.get(eventId);
      if (unsubscribe) {
        unsubscribe();
        eventSubscriptionsRef.current.delete(eventId);
        setSubscribedEventIds(prev => {
          if (!prev.has(eventId)) return prev;
          const next = new Set(prev);
          next.delete(eventId);
          return next;
        });
      }
    }

    const event = eventsRef.current.find(e => e.id === eventId);
    if (!event || !event.timeslotId) return;

    setLoadingEvents(prev => new Set([...prev, eventId]));
    loadingQueueRef.current.add(eventId);

    try {
      console.log(`📡 Loading event details via REST: ${eventId} (forceRefresh: ${forceRefresh})`);
      eventsLoadedViaHTTP.current.add(eventId);
      await loadEventDetailsHTTP(eventId, forceRefresh);

      setEventLoadErrors(prev => {
        if (!prev[eventId]) return prev;
        const next = { ...prev };
        delete next[eventId];
        return next;
      });

      // Upgrade to WS for live updates now that REST data has landed
      subscribeEventForUpdates(eventId);
    } catch (err) {
      console.error(`Error loading details for event ${eventId}:`, err);
      setEventLoadErrors(prev => ({ ...prev, [eventId]: err.message || 'Failed to load' }));
    } finally {
      setLoadingEvents(prev => {
        const newSet = new Set(prev);
        newSet.delete(eventId);
        return newSet;
      });
      loadingQueueRef.current.delete(eventId);
    }
  }, [loadingEvents, loadEventDetailsHTTP, subscribeEventForUpdates]);

  // Load a past event's full detail (tier breakdown) on EXPAND. Past rows are
  // static PG archives — no WS — so this is a fast /past-events/:id PK lookup
  // that replaces the header-only card with the full-detail version.
  const loadPastEventDetail = useCallback(async (eventId) => {
    const ev = pastEventsRef.current.find(e => e.id === eventId);
    if (!ev || !ev.isHeaderOnly) return;                 // already has detail
    if (loadingPastDetailRef.current.has(eventId)) return; // in flight
    loadingPastDetailRef.current.add(eventId);
    setLoadingEvents(prev => new Set([...prev, eventId]));
    try {
      const raw = await fetchPastEventDetail(eventId);
      const processed = processPastEventDetail(raw);      // detail_data present → full breakdown
      setPastEvents(prev => {
        const next = prev.map(e => (e.id === eventId ? processed : e));
        pastEventsRef.current = next;
        return next;
      });
    } catch (err) {
      console.error(`Error loading past event detail ${eventId}:`, err.message);
    } finally {
      loadingPastDetailRef.current.delete(eventId);
      setLoadingEvents(prev => { const n = new Set(prev); n.delete(eventId); return n; });
    }
  }, [processPastEventDetail]);

  // Batch REST loader: fetches details for multiple events in one request.
  // Used on initial mount and when many cards enter the viewport at once.
  const loadEventDetailsBatch = useCallback(async (eventIds) => {
    const newIds = eventIds.filter(id =>
      !loadingEvents.has(id) &&
      !loadingQueueRef.current.has(id)
    );
    if (newIds.length === 0) return;

    const eventsToFetch = newIds
      .map(id => eventsRef.current.find(e => e.id === id))
      .filter(e => e && e.timeslotId)
      .map(e => ({ eventId: e.id, timeslotId: e.timeslotId }));

    if (eventsToFetch.length === 0) return;

    eventsToFetch.forEach(({ eventId }) => loadingQueueRef.current.add(eventId));
    setLoadingEvents(prev => {
      const next = new Set(prev);
      eventsToFetch.forEach(({ eventId }) => next.add(eventId));
      return next;
    });

    try {
      console.log(`📡 Batch-loading ${eventsToFetch.length} event details via REST`);
      const { details, errors } = await fetchEventDetailsBatch(eventsToFetch);

      Object.entries(details).forEach(([eventId, detail]) => {
        processEventDetailData(eventId, detail);
        eventsLoadedViaHTTP.current.add(eventId);
      });

      setEventLoadErrors(prev => {
        const next = { ...prev };
        Object.keys(details).forEach(id => { delete next[id]; });
        Object.entries(errors).forEach(([id, err]) => {
          next[id] = err.message || `Failed (status ${err.status || '?'})`;
        });
        return next;
      });

      // Upgrade successful events to WS for live updates
      Object.keys(details).forEach(eventId => subscribeEventForUpdates(eventId));
    } catch (err) {
      console.error('Batch load failed:', err);
      setEventLoadErrors(prev => {
        const next = { ...prev };
        eventsToFetch.forEach(({ eventId }) => {
          next[eventId] = err.message || 'Batch load failed';
        });
        return next;
      });
    } finally {
      setLoadingEvents(prev => {
        const next = new Set(prev);
        eventsToFetch.forEach(({ eventId }) => next.delete(eventId));
        return next;
      });
      eventsToFetch.forEach(({ eventId }) => loadingQueueRef.current.delete(eventId));
    }
  }, [loadingEvents, processEventDetailData, subscribeEventForUpdates]);

  // ALL your original toggle functions - preserved exactly
  const toggleEventExpansion = (eventId) => {
    setExpandedEvents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(eventId)) {
        newSet.delete(eventId);
      } else {
        newSet.add(eventId);
        // Past events load their detail on EXPAND (upcoming load via the scroll
        // observer + WS). No-op if this card already has its detail.
        const pe = pastEventsRef.current.find(e => e.id === eventId);
        if (pe && pe.isPastEvent && pe.isHeaderOnly) loadPastEventDetail(eventId);
      }
      return newSet;
    });
  };

  const togglePriceDisplay = (eventId) => {
    setEventPriceDisplay(prev => ({
      ...prev,
      [eventId]: !prev[eventId]
    }));
  };

  const toggleRevenueDisplay = (eventId) => {
    setEventRevenueDisplay(prev => {
      const current = prev[eventId] || 'revenue';
      let next;
      if (current === 'revenue') {
        next = 'base';
      } else if (current === 'base') {
        next = 'allin';
      } else {
        next = 'revenue';
      }
      return {
        ...prev,
        [eventId]: next
      };
    });
  };

  const toggleSection = (eventId, section, forceOpen = false) => {
    setExpandedSections(prev => {
      const evSections = { ...(prev[eventId] || {}) };
      evSections[section] = forceOpen ? true : !evSections[section];
      return { ...prev, [eventId]: evSections };
    });
  };

  const toggleAdSetExpansion = (campaignId, adsetId) => {
    const key = `${campaignId}-${adsetId}`;
    setExpandedAdSets(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Automatic detail loading - exactly as before
  useEffect(() => {
    if (events.length === 0 || loadingQueueRef.current.size > 0) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const eventId = entry.target.getAttribute('data-event-id');
            const event = eventsRef.current.find(e => e.id === eventId);
            
            if (event && event.isHeaderOnly && !event.isPastEvent && !loadingEvents.has(eventId) && !eventSubscriptionsRef.current.has(eventId)) {
              loadEventDetails(eventId, false);
              // Stop observing this element once we start loading
              observer.unobserve(entry.target);
            }
          }
        });
      },
      {
        root: null,
        rootMargin: '100px',
        threshold: 0.1
      }
    );
    
    observerRef.current = observer;
    
    const cards = document.querySelectorAll('[data-event-id]');
    cards.forEach(card => observer.observe(card));
    
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [events, loadingEvents, loadEventDetails]);

  // Hybrid mode: when WS becomes available (or reconnects), promote any REST-loaded
  // events to a live WS subscription. Safety net for the case where REST loaded
  // before the WebSocket was connected.
  useEffect(() => {
    if (!(wsConnected && useWebSocketEnabled && eventsRef.current.length > 0)) return;
    const httpLoadedEvents = Array.from(eventsLoadedViaHTTP.current);
    if (httpLoadedEvents.length === 0) return;

    console.log(`🔄 Upgrading ${httpLoadedEvents.length} REST-loaded events to WebSocket for live updates`);
    httpLoadedEvents.forEach((eventId, index) => {
      setTimeout(() => subscribeEventForUpdates(eventId), index * 100);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsConnected, useWebSocketEnabled]); // Intentionally exclude unstable dependencies

  // Load event headers on mount - exactly as before
  useEffect(() => {
    const initialize = async () => {
      console.log('Starting initialization...');
      
      const timeout = setTimeout(() => {
        if (loading) {
          setError('Request timeout - please check your connection');
          setLoading(false);
        }
      }, 30000);
      
      try {
        const loadedEvents = await loadEventHeaders();
        
        loadMetaCampaigns().catch(err => {
          console.error('Error loading Meta campaigns:', err);
          setMetaApiError(err.message);
        });
        
        if (loadedEvents && loadedEvents.length > 0) {
          // Batch-load the first N events in one REST call so the dashboard renders fast.
          // IntersectionObserver picks up the rest as they scroll into view.
          const firstBatch = loadedEvents.slice(0, 8).map(e => e.id);
          loadEventDetailsBatch(firstBatch);
        }
      } catch (err) {
        console.error('Initialization error:', err);
        setError(err.message);
      } finally {
        clearTimeout(timeout);
        setLoading(false);
      }
    };
    
    initialize();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup event detail subscriptions on unmount
  useEffect(() => {
    return () => {
      const subscriptions = eventSubscriptionsRef.current;
      console.log(`🧹 Cleaning up ${subscriptions.size} event detail subscriptions`);
      // Unsubscribe from all event details
      subscriptions.forEach((unsubscribe, eventId) => {
        console.log(`🔕 Unsubscribing from event detail: ${eventId}`);
        unsubscribe();
      });
      subscriptions.clear();
    };
  }, []);

  // Apply dark mode to <html> (document.documentElement), NOT <body>:
  // teliance-events' Tailwind is darkMode:['class'], so `dark:` variants and
  // lib/theme.js both key off `.dark` on <html>. Toggling body had no effect.
  useEffect(() => {
    const root = document.documentElement;
    if (darkMode) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [darkMode]);

  // Close header menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerMenuRef.current && !headerMenuRef.current.contains(event.target)) {
        setShowHeaderMenu(false);
      }
    };

    if (showHeaderMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showHeaderMenu]);

  // Refresh data when app becomes visible (PWA reopened)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (!document.hidden) {
        console.log('📱 App became visible - refreshing data...');

        // Reload event headers
        try {
          await loadEventHeaders();

          // Refresh expanded events
          const expandedEventIds = Array.from(expandedEvents);
          for (const eventId of expandedEventIds) {
            if (!loadingEvents.has(eventId)) {
              await loadEventDetails(eventId, true);
            }
          }

          // Refresh Meta campaigns
          await loadMetaCampaigns();
        } catch (err) {
          console.error('Error refreshing data on visibility change:', err);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadEventHeaders, loadEventDetails, loadMetaCampaigns, expandedEvents, loadingEvents]);

  // Set up WebSocket events list subscription - exactly as before
  useEffect(() => {
    console.log(`📡 Events subscription effect - wsConnected: ${wsConnected}, useWebSocketEnabled: ${useWebSocketEnabled}`);
    if (wsConnected && useWebSocketEnabled) {
      console.log('📡 Setting up WebSocket events list subscription');
      
      const unsubscribe = subscribeToEvents(
        UPCOMING_EVENTS_QUERY,
        {
          hostId: HOST_ID,
          limit: 300
        },
        (data) => {
          console.log('📡 Received events list update via WebSocket');
          
          if (data.errors) {
            console.error('Events list GraphQL errors:', data.errors);
            return;
          }
          
          if (data.data?.host?.events?.nodes) {
            const upcomingEvents = data.data.host.events.nodes;
            const processedEvents = processEventsData(upcomingEvents);

            // Functional setEvents - the merge MUST read the latest committed state.
            // A stale eventsRef here is the price-flicker race: when a webhook fires both
            // event_detail_update and events_update back-to-back, the detail callback
            // queues a setEvents (new prices). If we read eventsRef.current before that
            // commits, our merge sees isHeaderOnly=true and clobbers the pending detail
            // update with zero-priced header-only data.
            setEvents(prevEvents => {
              const existingEventsMap = new Map(prevEvents.map(e => [e.id, e]));
              const merged = processedEvents.map(newEvent => {
                const existing = existingEventsMap.get(newEvent.id);
                if (existing && !existing.isHeaderOnly) {
                  return existing;
                }
                return newEvent;
              });
              eventsRef.current = merged;
              return merged;
            });
            setCacheStatus(data._cacheStatus ? {
              status: data._cacheStatus,
              timestamp: data._cachedAt || data._fetchedAt,
              warning: data._staleWarning
            } : null);
          }
        }
      );
      
      return () => {
        console.log('📡 Cleaning up WebSocket events list subscription');
        unsubscribe();
      };
    }
  }, [wsConnected, useWebSocketEnabled, subscribeToEvents, processEventsData]);

  // HTTP Polling fallback for event updates
  // When WebSocket is not connected, poll for updates every 30 seconds
  useEffect(() => {
    // Clear any existing interval
    if (eventPollingIntervalRef.current) {
      clearInterval(eventPollingIntervalRef.current);
      eventPollingIntervalRef.current = null;
    }

    // Only poll if WebSocket is not connected or disabled
    if (!wsConnected || !useWebSocketEnabled) {
      console.log('🔄 Starting event polling (WebSocket not connected)');
      
      // Poll every 30 seconds
      eventPollingIntervalRef.current = setInterval(async () => {
        console.log('🔄 Polling for event updates...');
        try {
          await loadEventHeaders();
          
          // Also refresh any currently expanded events
          const expandedEventIds = Array.from(expandedEvents);
          for (const eventId of expandedEventIds) {
            if (!loadingEvents.has(eventId)) {
              await loadEventDetails(eventId, true);
            }
          }
        } catch (err) {
          console.error('Error during event polling:', err);
        }
      }, 30000); // 30 seconds
    } else {
      console.log('✅ WebSocket connected - disabling event polling');
    }

    return () => {
      if (eventPollingIntervalRef.current) {
        clearInterval(eventPollingIntervalRef.current);
        eventPollingIntervalRef.current = null;
      }
    };
  }, [wsConnected, useWebSocketEnabled, loadEventHeaders, loadEventDetails, expandedEvents, loadingEvents]);

  // ALL your handlers - preserved exactly
  // Pull-to-refresh action (was dead code until the PTR gesture wired it up)
  const handleRefresh = async () => {
    setError(null);
    setIsUpdating(true);
    
    try {
      await loadEventHeaders();
      await loadMetaCampaigns();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRefreshEvent = async (eventId) => { // eslint-disable-line no-unused-vars
    const event = events.find(e => e.id === eventId);
    if (!event) return;
    
    if (wsConnected && useWebSocketEnabled && eventSubscriptionsRef.current.has(eventId)) {
      console.log(`📡 Event ${eventId} has active WebSocket subscription - updates will arrive automatically`);
      return;
    }
    
    await loadEventDetails(eventId, true);
  };

  const toggleWebSocketUsage = () => {
    setUseWebSocketEnabled(!useWebSocketEnabled);
  };

  // Save notification history to localStorage whenever it changes
  useEffect(() => {
    try {
      // Keep only the last 100 notifications to prevent localStorage from growing too large
      const limited = notificationHistory.slice(0, 100);
      localStorage.setItem('notificationHistory', JSON.stringify(limited));
    } catch (err) {
      console.error('Failed to save notification history:', err);
    }
  }, [notificationHistory]);

  const clearNotificationHistory = () => {
    setNotificationHistory([]);
    localStorage.removeItem('notificationHistory');
  };

  // Drag and drop handlers
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (over && active.id !== over.id) {
      const oldIndex = matchedEvents.findIndex(e => e.id === active.id);
      const newIndex = matchedEvents.findIndex(e => e.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(matchedEvents, oldIndex, newIndex).map(e => e.id);
        setCustomEventOrder(newOrder);
        localStorage.setItem('customEventOrder', JSON.stringify(newOrder));
        console.log('🔄 Event order updated:', newOrder);
      }
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const resetEventOrder = () => {
    setCustomEventOrder(null);
    localStorage.removeItem('customEventOrder');
    console.log('🔄 Event order reset to default (by date)');
  };

  const handleSaveOrder = (newOrder) => {
    setCustomEventOrder(newOrder);
    localStorage.setItem('customEventOrder', JSON.stringify(newOrder));
    console.log('💾 Event order saved from modal:', newOrder);
  };

  const applyEventFilters = (eventList) => {
    return eventList.filter(event => {
      const matchesSearch = (event.title || '').toLowerCase().includes(searchTerm.toLowerCase());
      if (venueFilter) {
        const eventTitle = event.title?.toLowerCase() || '';
        const eventVenue = event.venue?.name?.toLowerCase() || '';
        return matchesSearch && (eventTitle.includes(venueFilter) || eventVenue.includes(venueFilter));
      }
      return matchesSearch;
    });
  };

  // When searching, show results from both upcoming and past events.
  // Upcoming is filtered client-side (small in-memory set); past is already
  // filtered/sorted/paginated server-side, so it's used as-is.
  const isSearching = searchTerm.trim().length > 0;
  const filteredUpcoming = applyEventFilters(events);

  // Determine which events to show
  let filteredEvents;
  let skipClientSort = false;
  if (isSearching) {
    // Merge upcoming + past search results (upcoming first)
    filteredEvents = [...filteredUpcoming, ...pastEvents];
  } else if (activeTab === 'past') {
    filteredEvents = pastEvents;
    skipClientSort = true; // server already ordered this page set
  } else {
    filteredEvents = filteredUpcoming;
  }

  // Apply sorting (client-side for upcoming / merged search; the pure past tab
  // is already sorted server-side, so skip it there).
  if (!skipClientSort && (sortBy !== 'date' || sortDir !== 'desc')) {
    filteredEvents = [...filteredEvents].sort((a, b) => {
      let valA, valB;
      if (sortBy === 'date') {
        valA = a.date || '';
        valB = b.date || '';
      } else if (sortBy === 'revenue') {
        valA = a.baseRevenue || 0;
        valB = b.baseRevenue || 0;
      } else if (sortBy === 'sold') {
        valA = a.sold || 0;
        valB = b.sold || 0;
      } else if (sortBy === 'avg') {
        valA = a.sold > 0 ? (a.baseRevenue || 0) / a.sold : 0;
        valB = b.sold > 0 ? (b.baseRevenue || 0) / b.sold : 0;
      }
      const cmp = valA < valB ? -1 : valA > valB ? 1 : 0;
      return sortDir === 'desc' ? -cmp : cmp;
    });
  }

  // Apply event limit if specified
  const limitedEvents = eventLimit ? filteredEvents.slice(0, eventLimit) : filteredEvents;

  // Apply custom ordering if it exists
  let orderedEvents = limitedEvents;
  if (customEventOrder && customEventOrder.length > 0) {
    // Create a map for quick lookup
    const eventsMap = new Map(limitedEvents.map(e => [e.id, e]));

    // Start with events in custom order
    orderedEvents = customEventOrder
      .map(id => eventsMap.get(id))
      .filter(e => e !== undefined); // Remove any events that no longer exist

    // Add any new events that aren't in the custom order yet (append to end)
    const orderedIds = new Set(orderedEvents.map(e => e.id));
    const newEvents = limitedEvents.filter(e => !orderedIds.has(e.id));
    orderedEvents = [...orderedEvents, ...newEvents];
  }

  const matchedEvents = orderedEvents.map(event => {
    const campaigns = matchEventWithCampaigns(event, metaCampaigns);
    return { ...event, matchedCampaigns: campaigns, widgetStats: { liveNow: liveCounts[event.id] || 0, hasData: widgetEventIds.has(event.id) } };
  });

  // Total people browsing the widget right now, across all events.
  const totalLive = Object.values(liveCounts).reduce((s, n) => s + (n || 0), 0);

  // The "live" event = one whose doors window is open now: from 2h BEFORE start
  // (user: data+icon available AS the event starts, with doors lead-in) through
  // ~6h after. Only this event gets occupancy polling + the flip affordance.
  const liveWindowEvent = useMemo(() => {
    const now = Date.now();
    return events.find(e => {
      if (!e.startAt) return false;
      const start = new Date(e.startAt).getTime();
      if (Number.isNaN(start)) return false;
      return now >= start - 2 * 3600 * 1000 && now <= start + 6 * 3600 * 1000;
    }) || null;
  }, [events]);

  // Poll live door occupancy for the in-window event — 60s ambient (server
  // caches 15s; ~1 upstream sweep/min shared by all viewers), 15s while flipped.
  useEffect(() => {
    if (!liveWindowEvent) { setOccupancy(null); return; }
    let alive = true;
    const load = () => fetchOccupancy(liveWindowEvent)
      .then((d) => { if (alive) setOccupancy(d?.available ? d : null); })
      .catch(() => {});
    load();
    const t = setInterval(load, flippedEventId ? 15000 : 60000);
    return () => { alive = false; clearInterval(t); };
  }, [liveWindowEvent, flippedEventId]);

  // Lock native overscroll for THIS route only: kills the iOS rubber-band
  // (sticky header dragging down) and Chrome-Android's native pull-refresh so
  // the custom gesture below replaces both. Restored on unmount.
  useEffect(() => {
    const prevHtml = document.documentElement.style.overscrollBehaviorY;
    const prevBody = document.body.style.overscrollBehaviorY;
    document.documentElement.style.overscrollBehaviorY = 'none';
    document.body.style.overscrollBehaviorY = 'none';
    return () => {
      document.documentElement.style.overscrollBehaviorY = prevHtml;
      document.body.style.overscrollBehaviorY = prevBody;
    };
  }, []);

  // Pull-to-refresh gesture: only arms when the page is scrolled to the very
  // top; mid-list pulls scroll normally. Desktop (no touch) unaffected.
  const onPullStart = (e) => {
    if (window.scrollY <= 0 && !isUpdating) {
      pullStartYRef.current = e.touches[0].clientY;
    } else {
      pullStartYRef.current = null;
    }
  };
  const onPullMove = (e) => {
    if (pullStartYRef.current === null) return;
    const dy = e.touches[0].clientY - pullStartYRef.current;
    if (dy > 0 && window.scrollY <= 0) {
      setPullDistance(Math.min(dy * 0.5, 90)); // resistance curve, capped
    } else if (dy <= 0) {
      setPullDistance(0);
    }
  };
  const onPullEnd = () => {
    if (pullStartYRef.current === null) return;
    const shouldRefresh = pullDistance > PULL_THRESHOLD;
    pullStartYRef.current = null;
    setPullDistance(0);
    if (shouldRefresh) handleRefresh();
  };

  return (
    <div
      className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors"
      onTouchStart={onPullStart}
      onTouchMove={onPullMove}
      onTouchEnd={onPullEnd}
    >
      {/* Update Available Banner */}
      {updateAvailable && (
        <UpdateAvailable
          onUpdate={handleUpdate}
          onDismiss={handleDismissUpdate}
        />
      )}

      {/* Stale-on-error banner, Universe was unreachable, so these numbers are
          from cache and may be out of date. Surfaces so nobody acts on them. */}
      {cacheStatus?.status === 'stale-on-error' && (
        <div className="bg-amber-100 dark:bg-amber-900/40 border-b border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-200 px-4 py-2 text-sm text-center">
          ⚠ Showing cached data, Universe is temporarily unavailable; these numbers may be out of date.
          {cacheStatus.timestamp ? ` Last updated ${new Date(cacheStatus.timestamp).toLocaleTimeString()}.` : ''}
        </div>
      )}

      {/* Toast, in-UI feedback (alert() is prohibited) */}
      {toast && (
        <div className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-50 max-w-md w-[calc(100%-2rem)] px-4 py-3 rounded-lg shadow-lg text-sm flex items-start gap-2 ${
          toast.type === 'error' ? 'bg-red-600 text-white'
            : toast.type === 'success' ? 'bg-green-600 text-white'
            : 'bg-gray-800 text-gray-100 dark:bg-gray-700'
        }`}>
          <span className="flex-1 whitespace-pre-line">{toast.message}</span>
          <button onClick={() => setToast(null)} className="font-bold opacity-70 hover:opacity-100">✕</button>
        </div>
      )}

      {/* Initial-load failure, visible + self-retrying (never a silent empty grid) */}
      {error && events.length === 0 && !loading && (
        <div className="bg-red-100 dark:bg-red-900/40 border-b border-red-300 dark:border-red-700 text-red-900 dark:text-red-200 px-4 py-2 text-sm text-center">
          ⚠ Couldn't load events: {error}, retrying automatically…
          <button
            onClick={() => { setError(null); setLoading(true); loadEventHeaders().catch(e => setError(e.message)).finally(() => setLoading(false)); }}
            className="ml-2 underline font-medium"
          >Retry now</button>
        </div>
      )}

      {/* Header */}
      {!hideHeader && (
        <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10 transition-colors">
        <div className="px-4 py-2">
          <div className="flex justify-between items-center mb-2 gap-2">
            <div className="flex items-center gap-2">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 whitespace-nowrap">Box Office Live</h1>
              {window.location.protocol === 'http:' && (
                <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 rounded-full">
                  DEV
                </span>
              )}
              {/* Total live across all events, sum of per-event live counts */}
              <span
                title={`${totalLive} ${totalLive === 1 ? 'person' : 'people'} browsing the widget right now, across all events`}
                className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${
                  totalLive > 0
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                    : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${totalLive > 0 ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
                {totalLive} live
              </span>
            </div>
            
            <div className="flex items-center gap-1 sm:gap-2">
              {/* Dark Mode Toggle */}
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title={darkMode ? "Switch to light mode" : "Switch to dark mode"}
              >
                {darkMode ? (
                  <Sun className="w-4 h-4 text-yellow-500" />
                ) : (
                  <Moon className="w-4 h-4 text-gray-600" />
                )}
              </button>

              {/* Audio Notifications Toggle */}
              <button
                onClick={() => {
                  const newState = !audioEnabled;
                  console.log(`🔊 Audio alerts ${newState ? 'enabled' : 'disabled'}`);
                  setAudioEnabled(newState);
                }}
                className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title={audioEnabled ? "Mute notifications" : "Enable audio notifications"}
              >
                {audioEnabled ? (
                  <Volume2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                ) : (
                  <VolumeX className="w-4 h-4 text-gray-400 dark:text-gray-600" />
                )}
              </button>

              {/* Notification History Component */}
              <NotificationHistory
                notifications={notificationHistory}
                onClearHistory={clearNotificationHistory}
              />

              {/* Hamburger Menu */}
              <div className="relative" ref={headerMenuRef}>
                <button
                  onClick={() => setShowHeaderMenu(!showHeaderMenu)}
                  className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  title="More options"
                >
                  <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                </button>

                {/* Dropdown Menu */}
                {showHeaderMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50">
                    {/* Push Notifications Toggle in dropdown */}
                    {pushNotificationManager.isSupported() ? (
                      <button
                        onClick={() => {
                          handleTogglePushNotifications();
                          setShowHeaderMenu(false);
                        }}
                        disabled={pushSubscribing}
                        className={`w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2 ${pushSubscribing ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {pushEnabled ? (
                          <Bell className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                        ) : (
                          <BellOff className="w-4 h-4 text-gray-400 dark:text-gray-600" />
                        )}
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {pushEnabled ? 'Disable Push Notifications' : 'Enable Push Notifications'}
                        </span>
                      </button>
                    ) : pushNotificationManager.isIOSSafariNotPWA() && (
                      <button
                        onClick={() => {
                          showToast('📱 iOS: tap Share (⎋) → "Add to Home Screen", open the app from your home screen, then enable push notifications.', 'info', 15000);
                          setShowHeaderMenu(false);
                        }}
                        className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
                      >
                        <BellOff className="w-4 h-4 text-orange-400 dark:text-orange-500" />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Push Notifications (iOS Setup)</span>
                      </button>
                    )}

                    {/* System Health - clickable item in dropdown */}
                    <SystemHealth
                      loading={loading}
                      loadingMetaCampaigns={loadingMetaCampaigns}
                      isUpdating={isUpdating}
                      metaApiError={metaApiError}
                      cacheStatus={cacheStatus}
                      connectionMethod={connectionMethod}
                      wsConnected={wsConnected}
                      wsStatus={wsStatus}
                      connectionQuality={connectionQuality}
                      subscriptionCount={subscriptionCount}
                      connectedClients={connectedClients}
                      useWebSocketEnabled={useWebSocketEnabled}
                      onToggleWebSocket={toggleWebSocketUsage}
                      onReconnectWebSocket={wsReconnect}
                      inMenu={true}
                    />

                    {/* Check for Updates Button */}
                    <button
                      onClick={async () => {
                        setShowHeaderMenu(false);
                        console.log('[App] Manual update check requested');
                        try {
                          // Use the new checkForUpdates method
                          const result = await pushNotificationManager.checkForUpdates();
                          console.log('[App] Update check result:', result);

                          if (result.error) {
                            showToast('Failed to check for updates: ' + result.error, 'error');
                          } else if (result.hasUpdate) {
                            console.log('[App] ✅ Update available:', result.currentVersion, '->', result.newVersion);
                            // Update banner will be shown automatically by the checkForUpdates method
                          } else {
                            console.log('[App] ℹ️ No update available - already on latest version');
                            const versionInfo = result.currentVersion ?
                              ` (v${result.currentVersion})` : '';
                            showToast(`You already have the latest version!${versionInfo}`, 'success');
                          }
                        } catch (err) {
                          console.error('[App] Update check failed:', err);
                          showToast('Failed to check for updates: ' + err.message, 'error');
                        }
                      }}
                      className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
                    >
                      <RefreshCw className="w-4 h-4 text-green-600 dark:text-green-400" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Check for Updates</span>
                    </button>

                    {/* Reorder Events Button */}
                    <button
                      onClick={() => {
                        setShowOrderModal(true);
                        setShowHeaderMenu(false);
                      }}
                      className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
                    >
                      <List className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Reorder Events</span>
                    </button>

                    {/* Connection Debug Button */}
                    <button
                      onClick={() => {
                        setShowDebugPanel(true);
                        setShowHeaderMenu(false);
                      }}
                      className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
                    >
                      <RefreshCw className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Connection Debug</span>
                    </button>

                    {/* Reset Order Button - show only when custom order is active */}
                    {customEventOrder && customEventOrder.length > 0 && (
                      <button
                        onClick={() => {
                          resetEventOrder();
                          setShowHeaderMenu(false);
                        }}
                        className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
                      >
                        <RotateCcw className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Reset Order</span>
                      </button>
                    )}

                    {/* App version, moved out of the header */}
                    <div className="px-3 py-2 mt-1 border-t border-gray-100 dark:border-gray-700 text-[11px] text-gray-400 dark:text-gray-500">
                      Version {APP_VERSION}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Search Bar */}
          <div className="relative">
            <input
              id="event-search"
              name="event-search"
              type="text"
              placeholder="Search events..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-1.5 pl-8 text-base border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              autoComplete="off"
            />
            <svg 
              className="absolute left-2.5 top-2 w-3.5 h-3.5 text-gray-400 dark:text-gray-500" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" 
              />
            </svg>
          </div>
        </div>
      </div>
      )}

      {/* Pull-to-refresh reveal: an expanding gap between header and list.
          Height tracks the finger while dragging (no transition), springs
          shut on release; holds at 48px with a spinner while refreshing. */}
      {(pullDistance > 0 || isUpdating) && (
        <div
          className="flex items-end justify-center overflow-hidden text-xs text-gray-500 dark:text-gray-400"
          style={{
            height: isUpdating ? 48 : pullDistance,
            transition: pullStartYRef.current !== null ? 'none' : 'height 0.25s ease',
          }}
        >
          <div className="flex items-center gap-2 pb-3">
            <RefreshCw
              className={`w-4 h-4 ${isUpdating ? 'animate-spin text-blue-600' : pullDistance > PULL_THRESHOLD ? 'text-blue-600' : ''}`}
              style={!isUpdating ? { transform: `rotate(${pullDistance * 3}deg)` } : undefined}
            />
            <span className={pullDistance > PULL_THRESHOLD && !isUpdating ? 'text-blue-600 font-medium' : ''}>
              {isUpdating ? 'Refreshing…' : pullDistance > PULL_THRESHOLD ? 'Release to refresh' : 'Pull to refresh'}
            </span>
          </div>
        </div>
      )}

      {/* Tab Bar */}
      {!hideHeader && (
        <div className="px-4 pt-2 flex items-center justify-between">
          <div className="flex gap-1.5">
            <button
              onClick={() => setActiveTab('upcoming')}
              className={`px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                activeTab === 'upcoming'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              Upcoming
              {events.length > 0 && (
                <span className="ml-1 opacity-75">({events.length})</span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('past')}
              className={`px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                activeTab === 'past'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              Past
              {pastTotal > 0 && (
                <span className="ml-1 opacity-75">({pastTotal})</span>
              )}
            </button>
          </div>

          {/* Sort controls */}
          <div className="flex items-center gap-1">
            {['date', 'revenue', 'sold', 'avg'].map(field => (
              <button
                key={field}
                onClick={() => {
                  if (sortBy === field) {
                    setSortDir(d => d === 'desc' ? 'asc' : 'desc');
                  } else {
                    setSortBy(field);
                    setSortDir('desc');
                  }
                }}
                className={`px-1.5 py-0.5 text-[11px] font-medium rounded transition-colors ${
                  sortBy === field
                    ? 'bg-gray-700 dark:bg-gray-300 text-white dark:text-gray-900'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {({ date: 'Date', revenue: 'Rev', sold: 'Sold', avg: 'Avg' })[field]}
                {sortBy === field && (sortDir === 'desc' ? '↓' : '↑')}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Events List - Updated with responsive grid and drag-and-drop */}
      {activeTab === 'upcoming' && !isSearching ? (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext
          items={matchedEvents.map(e => e.id)}
          strategy={rectSortingStrategy}
        >
          <div className="px-4 py-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 auto-rows-auto items-start">
            {matchedEvents.map(event => {
              const isLoading = loadingEvents.has(event.id);
              const isExpanded = expandedEvents.has(event.id);
              const cacheTime = event._cachedAt ? new Date(event._cachedAt) : eventLastRefreshed[event.id];
              const showAllInPrices = eventPriceDisplay[event.id];
              const revenueDisplayType = eventRevenueDisplay[event.id] || 'revenue';
              const hasRecentUpdate = recentlyUpdatedEvents.has(event.id);
              const ticketChange = ticketChanges[event.id] || 0;
              const categoryChange = categoryChanges[event.id] || {};
              const isSubscribed = subscribedEventIds.has(event.id);
              const isDragging = activeId === event.id;

              // Live occupancy flip: only the event matching tonight's
              // security check-in link gets a back face + the flip toggle.
              const occMatch = occupancy && occupancy.eventId === event.id ? occupancy : null;
              const isFlipped = !!occMatch && flippedEventId === event.id;
              const card = (
                <EventCard
                  key={occMatch ? undefined : event.id}
                  event={event}
                  isLoading={isLoading}
                  loadError={eventLoadErrors[event.id] || null}
                  isExpanded={isExpanded}
                  isPastEvent={event.isPastEvent || false}
                  showAllInPrices={showAllInPrices}
                  revenueDisplayType={revenueDisplayType}
                  hasRecentUpdate={hasRecentUpdate}
                  ticketChange={ticketChange}
                  categoryChange={categoryChange}
                  sectionExpanded={expandedSections}
                  expandedAdSets={expandedAdSets}
                  matchedCampaigns={event.matchedCampaigns || []}
                  widgetStats={event.widgetStats || null}
                  darkMode={darkMode}
                  cacheTime={cacheTime}
                  wsConnected={wsConnected}
                  isSubscribed={isSubscribed}
                  isDragging={isDragging}
                  occupancyAvailable={!!occMatch}
                  onToggleFlip={() => setFlippedEventId(isFlipped ? null : event.id)}
                  onToggleExpansion={() => toggleEventExpansion(event.id)}
                  onRefresh={(forceRefresh) => loadEventDetails(event.id, forceRefresh)}
                  onRetry={() => loadEventDetails(event.id, true)}
                  onTogglePriceDisplay={() => togglePriceDisplay(event.id)}
                  onToggleRevenueDisplay={() => toggleRevenueDisplay(event.id)}
                  onToggleSection={toggleSection}
                  onToggleAdSetExpansion={toggleAdSetExpansion}
                />
              );
              if (!occMatch) return card;
              // Only the VISIBLE face sits in normal flow (defines the card's
              // height); the hidden face is an absolute overlay so the tall
              // occupancy face doesn't stretch the grid row while unflipped
              // (and vice versa). Backface culling keeps the overlay invisible.
              const hiddenFace = { position: 'absolute', inset: 0, overflow: 'hidden' };
              return (
                <div key={event.id} style={{ perspective: '1500px' }}>
                  <div
                    className="relative transition-transform duration-500"
                    style={{ transformStyle: 'preserve-3d', transform: isFlipped ? 'rotateY(180deg)' : 'none' }}
                  >
                    <div style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden', ...(isFlipped ? hiddenFace : {}) }}>
                      {card}
                    </div>
                    <div style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden', transform: 'rotateY(180deg)', ...(isFlipped ? {} : hiddenFace) }}>
                      <OccupancyCard event={event} data={occMatch} onFlipBack={() => setFlippedEventId(null)} />
                    </div>
                  </div>
                </div>
              );
            })}

            {matchedEvents.length === 0 && !loading && (
              <div className="col-span-full text-center py-12">
                <div className="text-gray-400 dark:text-gray-600 mb-2">
                  <Calendar className="w-12 h-12 mx-auto" />
                </div>
                <p className="text-gray-600 dark:text-gray-400">No events found</p>
              </div>
            )}
          </div>
        </SortableContext>
      </DndContext>
      ) : (
        /* Past events tab or search results (no drag-and-drop) */
        <div className="px-4 py-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 auto-rows-auto items-start">
          {pastEventsLoading && (
            <div className="col-span-full text-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
              <p className="text-gray-600 dark:text-gray-400">Loading past events...</p>
            </div>
          )}
          {matchedEvents.map(event => {
            const isExpanded = expandedEvents.has(event.id);
            const showAllInPrices = eventPriceDisplay[event.id];
            const revenueDisplayType = eventRevenueDisplay[event.id] || 'revenue';
            const eventSections = expandedSections;

            // Any past card of a door-data venue gets the flip affordance +
            // Shield; the historical face loads (or lazily composes, for events
            // with no stored snapshot yet) its data on first flip.
            const canFlip = !!event.hasOccupancy || occupancySupported;
            const isFlipped = canFlip && flippedEventId === event.id && !!historicalOcc[event.id];
            const pastCard = (
              <EventCard
                key={event.id}
                event={event}
                isLoading={false}
                isExpanded={isExpanded}
                showAllInPrices={showAllInPrices}
                revenueDisplayType={revenueDisplayType}
                hasRecentUpdate={false}
                ticketChange={0}
                categoryChange={{}}
                sectionExpanded={eventSections}
                expandedAdSets={expandedAdSets}
                matchedCampaigns={[]}
                widgetStats={event.widgetStats || null}
                darkMode={darkMode}
                cacheTime={null}
                wsConnected={false}
                isSubscribed={false}
                isDragging={false}
                isPastEvent={event.isPastEvent}
                occupancyAvailable={canFlip}
                onToggleFlip={() => flipPastCard(event.id)}
                onToggleExpansion={() => toggleEventExpansion(event.id)}
                onRefresh={() => {}}
                onTogglePriceDisplay={() => togglePriceDisplay(event.id)}
                onToggleRevenueDisplay={() => toggleRevenueDisplay(event.id)}
                onToggleSection={toggleSection}
                onToggleAdSetExpansion={toggleAdSetExpansion}
              />
            );
            if (!canFlip) return pastCard;
            // Visible face defines the height; hidden face overlays absolutely
            // so the tall recap face doesn't stretch the grid row when unflipped.
            const hiddenFace = { position: 'absolute', inset: 0, overflow: 'hidden' };
            return (
              <div key={event.id} style={{ perspective: '1500px' }}>
                <div className="relative transition-transform duration-500" style={{ transformStyle: 'preserve-3d', transform: isFlipped ? 'rotateY(180deg)' : 'none' }}>
                  <div style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden', ...(isFlipped ? hiddenFace : {}) }}>{pastCard}</div>
                  <div style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden', transform: 'rotateY(180deg)', ...(isFlipped ? {} : hiddenFace) }}>
                    {historicalOcc[event.id] && <OccupancyCard event={event} historical={historicalOcc[event.id]} onFlipBack={() => setFlippedEventId(null)} />}
                  </div>
                </div>
              </div>
            );
          })}
          {matchedEvents.length === 0 && !pastEventsLoading && !loading && (
            <div className="col-span-full text-center py-12">
              <div className="text-gray-400 dark:text-gray-600 mb-2">
                <Archive className="w-12 h-12 mx-auto" />
              </div>
              <p className="text-gray-600 dark:text-gray-400">
                {activeTab === 'past' ? 'No past events archived yet' : 'No events found'}
              </p>
            </div>
          )}
          {/* Infinite-scroll sentinel + "loading more" row for the past archive */}
          {pastHasMore && !pastEventsLoading && (
            <div ref={pastSentinelRef} className="col-span-full text-center py-6">
              {pastLoadingMore && (
                <div className="animate-spin w-6 h-6 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Multi-connection warning */}
      
      {/* Audio element for notifications */}
      <audio
        ref={audioRef}
        preload="auto"
        style={{ display: 'none' }}
      >
        <source src="/notification.mp3" type="audio/mpeg" />
      </audio>

      {/* Order Events Modal */}
      {showOrderModal && (
        <OrderEventsModal
          events={matchedEvents}
          currentOrder={customEventOrder}
          onSave={handleSaveOrder}
          onClose={() => setShowOrderModal(false)}
        />
      )}

      {/* Connection Debug Modal */}
      {showDebugPanel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Connection Debug</h2>
              <button
                onClick={() => setShowDebugPanel(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <pre className="text-xs font-mono bg-gray-50 dark:bg-gray-900 p-4 rounded border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-all">
{`=== CONNECTION DEBUG INFO ===

WebSocket URL: ${wsUrl || 'Not connected'}
WebSocket Status: ${wsStatus || 'unknown'}
Connected: ${wsConnected ? 'Yes' : 'No'}
Quality: ${connectionQuality || 'unknown'}
Error: ${wsError || 'None'}

=== DEVICE INFO ===

User Agent: ${navigator.userAgent}
Protocol: ${window.location.protocol}
Host: ${window.location.host}
Full URL: ${window.location.href}
Display Mode: ${window.matchMedia('(display-mode: standalone)').matches ? 'standalone (PWA)' : 'browser'}
iOS Device: ${/iPad|iPhone|iPod/.test(navigator.userAgent) ? 'Yes' : 'No'}
Standalone Mode: ${window.navigator.standalone || window.matchMedia('(display-mode: standalone)').matches ? 'Yes' : 'No'}

=== WEBSOCKET ENABLED ===

WebSocket Feature: ${useWebSocketEnabled ? 'Enabled' : 'Disabled'}
Connection Method: ${connectionMethod}
Active Subscriptions: ${subscriptionCount || 0}
Connected Clients: ${connectedClients || 0}

=== END DEBUG INFO ===`}
              </pre>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-between">
              <button
                onClick={() => {
                  const debugText = document.querySelector('.fixed pre').textContent;
                  navigator.clipboard.writeText(debugText).then(() => {
                    showToast('Debug info copied to clipboard!', 'success');
                  });
                }}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium"
              >
                Copy to Clipboard
              </button>
              <button
                onClick={() => setShowDebugPanel(false)}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 rounded text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDashboard;